/**
 * Mobile/tablet parallax — MiuMiu-style bottom-sheet product info reveal.
 *
 * Driven entirely by real scroll position via GSAP ScrollTrigger (pin + scrub + snap) —
 * no preventDefault, no manual scrollTo, no custom touch-drag hijacking. Native browser
 * scroll (with its own momentum/rubber-banding on iOS and Android) drives everything.
 * One scroll-progress value is the single source of truth for both the panel's reveal
 * state and the header's visibility during the transition, so they cannot drift out of
 * sync the way the old paired freeze()/thaw() flag system repeatedly did.
 *
 * Replaces the old custom drag/scroll/snap/header-freeze state machine that lived in this
 * IIFE. Everything below this IIFE (gallery-visibility toggle, --pdp-vh freeze, Kiwi
 * size-chart modal integration, size-popup, color-popup) is a separate, independent IIFE
 * and is untouched by this rewrite.
 */
(function () {
  function isMobile() {
    return window.innerWidth <= 1023;
  }
  function isKiwiModalOpen() {
    return !!window.__pdpKiwiModalOpen;
  }
  function isPdpSidebarOpen() {
    return typeof window.isPdpSidebarOpen === 'function'
      ? window.isPdpSidebarOpen()
      : !!document.querySelector('.pdp-sidebar.is-open');
  }

  // ── External header-lock hooks ──────────────────────────────────────────────
  // The Kiwi size-chart modal and size/color popup subsystems further down this file
  // call these two globals to hold the header visible while they're open (they predate
  // this rewrite and are left as-is per plan — only these two call targets change).
  // Simpler than the old freeze()/thaw() pair: no cached header height, no
  // transition-suppression/data-attribute dance, because there's no competing rapid
  // scrollTo animation to protect against here. Those subsystems lock page scroll
  // entirely while open, so the ScrollTrigger below simply receives no scroll events
  // (and its onUpdate never re-evaluates preventHide) until they close and release it —
  // the two mechanisms can't fight over the same flag because only one is ever "live"
  // (actually receiving scroll) at a time.
  function parallaxFreezeHeaderExternal() {
    var el = document.querySelector('sticky-header');
    if (!el) return;
    el.preventHide = true;
    el.preventReveal = true;
    if (el.header) {
      el.header.classList.remove('shopify-section-header-hidden');
      el.header.classList.add('shopify-section-header-sticky');
    }
  }
  function parallaxThawHeaderExternal() {
    var el = document.querySelector('sticky-header');
    if (!el) return;
    el.preventHide = false;
    el.preventReveal = false;
  }
  window._parallaxFreezeHeader = parallaxFreezeHeaderExternal;
  window._parallaxThawHeader = parallaxThawHeaderExternal;

  function init() {
    if (!isMobile()) return;

    var parallaxRoot = document.querySelector('.product-paralax');
    var infoWrapper = document.querySelector('.product-paralax .product__info-wrapper');
    var pinTarget = document.querySelector('.product-paralax .product__info-pin');
    var bar = document.querySelector('.product-paralax .product-parallax-bar');

    if (!infoWrapper || !pinTarget) {
      // Template without the parallax panel (e.g. shop-the-look) — sticky-header.js sets
      // preventHide=true for any mobile PDP template, expecting this script to eventually
      // clear it. Without this, preventHide stays stuck true for the page's whole
      // lifetime, freezing the header in whatever state it started.
      var hdrElNoParallax = document.querySelector('sticky-header');
      if (hdrElNoParallax) hdrElNoParallax.preventHide = false;
      return;
    }

    if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
      // GSAP failed to load (CDN/asset issue) — fail safe rather than leaving the panel
      // stuck collapsed with no way to open it: let it render as normal in-flow content.
      var hdrElNoGsap = document.querySelector('sticky-header');
      if (hdrElNoGsap) hdrElNoGsap.preventHide = false;
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    // Purpose-built for iOS Safari's dynamic toolbar firing spurious resize→refresh
    // cycles — replaces the old file's ad-hoc frozen --pdp-vh custom-property workaround.
    ScrollTrigger.config({ ignoreMobileResize: true });

    // Permanent on real iOS devices: transitions on info-wrapper/children force a
    // synchronous layout recalc every frame while --pdp-reveal-progress is scrubbing,
    // which is the flicker/stuck-mid-close reported on real iPhone (desktop/DevTools
    // emulation doesn't reproduce it because it never drives real per-frame native
    // scroll events the way rAF-driven close/snap does on-device). See the
    // .parallax--reduced-motion rules in section-main-product.css this class enables.
    if (parallaxRoot) parallaxRoot.classList.add('parallax--reduced-motion');

    // Other scripts (sticky-header.js, the gallery-visibility toggle below) skip their
    // own layout-forcing work while this is true, so it doesn't race the panel's own
    // rAF-driven scrollTo loop mid drag/snap.
    window._pdpParallaxIsBusy = function () {
      return isTouchingPanel || isAutoSnapping || !!barDrag || !!barSwipeGesture;
    };

    // Permanently cancel Shopify's own scroll-trigger reveal animation on the info-wrapper —
    // on mobile PDP the page-scroll IS the reveal; Shopify's IntersectionObserver-driven
    // fade/slide must never also run here or it flashes to opacity:0.01 whenever it re-fires.
    infoWrapper.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in', 'scroll-trigger--offscreen');
    infoWrapper.classList.add('scroll-trigger--cancel');
    infoWrapper.style.removeProperty('opacity');
    infoWrapper.style.removeProperty('transform');
    var _scrollTriggerMutObs = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type !== 'attributes' || m.attributeName !== 'class') return;
        var el = m.target;
        if (el.classList.contains('scroll-trigger') && !el.classList.contains('scroll-trigger--cancel')) {
          el.classList.add('scroll-trigger--cancel');
          el.classList.remove('scroll-trigger--offscreen');
          el.style.removeProperty('opacity');
          el.style.removeProperty('transform');
        }
      });
    });
    _scrollTriggerMutObs.observe(infoWrapper, { subtree: true, attributes: true, attributeFilter: ['class'] });

    // Same source the gallery's own `top: var(--header-height, 0px)` sticky rule reads
    // (assets/section-main-product.css, .product__media-wrapper) and the same source
    // .product__info-wrapper's own `position: sticky; top: var(--header-height, 0px)`
    // reads — keeping the JS-computed docking point and the CSS docking point identical
    // is what makes the two line up with no gap/overlap.
    function getHeaderOffset() {
      var v = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      var n = parseFloat(v);
      return isNaN(n) ? 0 : n;
    }

    // Reveal is driven purely by the panel's own on-screen position as it approaches the
    // header — NOT by first reaching/docking under the header and only then revealing off
    // a separate content-height distance. end: the moment it reaches its docked position
    // (matching .product__info-wrapper's `position: sticky; top: var(--header-height)` in
    // CSS — once native sticky takes over there, no JS/pin is involved, it just stays
    // put). No `pin` here at all: pin holds an element's SCREEN position constant, which
    // is the opposite of what this phase needs (the panel visibly rising up the screen);
    // sticky positioning handles "stay put" for the phase after this one.
    //
    // start is NOT 'top bottom' (the moment the panel's top edge enters the viewport) —
    // this theme deliberately sizes the gallery to `100svh - headerHeight - 22.7rem` (see
    // .product__media-wrapper in section-main-product.css) specifically so the peek row
    // (title/price/ATC) is already partially visible on page load, without scrolling.
    // That means the panel's top is already ABOVE the viewport's bottom edge at scrollY 0
    // — past the 'top bottom' threshold before the user does anything — which baked in a
    // nonzero reveal progress (~0.2) at rest instead of 0. Anchoring start to the actual
    // page-load scroll position fixes that by construction, regardless of how tall the
    // gallery happens to be on a given viewport.
    var initialScrollY = window.scrollY;
    var tween = gsap.fromTo(pinTarget,
      { '--pdp-reveal-progress': 0 },
      {
        '--pdp-reveal-progress': 1,
        ease: 'none',
        scrollTrigger: {
          id: 'pdp-panel-reveal',
          trigger: infoWrapper,
          start: function () { return initialScrollY; },
          end: function () { return 'top top+=' + Math.round(getHeaderOffset()); },
          // Small lag, not 0/true: the panel's own docking is native position:sticky, so
          // it locks to the header instantly regardless of this value — this only smooths
          // the content reveal (opacity/max-height). A bigger lag here was letting that
          // reveal visibly keep "catching up" for a moment after the panel had already
          // docked, reading as a jump/bounce right after arriving at state 1.
          scrub: 0.1,
          invalidateOnRefresh: true,
          onUpdate: updateHeaderVisibility,
        },
      }
    );
    var panelTrigger = tween.scrollTrigger;
    // Page loads at rest, collapsed (progress 0) — this is a no-op removal in that
    // case, but keeps behavior correct if it ever needed to run at a stale progress.
    updateFullyExpandedClass();

    // Kiwi's size-chart modal (and the size/color popups) lock page scroll by setting
    // body { position:fixed; top:-Npx }, which zeroes window.scrollY the instant it's
    // applied. This trigger's reveal is driven by real scroll position, so that zeroing
    // reads as "scrolled back to the top" and snaps --pdp-reveal-progress to 0 — the
    // expanded panel visually collapses back to its peek state (Add to Cart jumps up,
    // description/size/badges collapse to 0 height) while scroll is locked. Pausing the
    // trigger before the lock is applied freezes reveal progress at its current value;
    // resuming after scroll is restored recomputes it against the real position again.
    // Progress at the moment the lock engaged. The lock's whole job is to put scrollY
    // back to exactly this same spot on release — nothing about start/end or the scroll
    // position is supposed to change while it's held (refresh is blocked during the lock
    // too, see scheduleRefresh below) — so the correct resume value is just "whatever it
    // was", not something that needs recomputing from a live readback at all. Recomputing
    // from window.scrollY (even "corrected" against the caller's known target) was the
    // actual source of the iOS jump: iOS doesn't commit scrollTo() to window.scrollY
    // synchronously coming out of body{position:fixed}, so that recompute could land on
    // a slightly-off progress and snap to it. Remembering and restoring the exact value
    // has no such dependency — there's nothing left to compute, so nothing to get wrong.
    var pausedProgress = null;
    window._parallaxPausePanelReveal = function () {
      pausedProgress = panelTrigger.progress;
      panelTrigger.disable(false);
      // Color swatches (and any other content whose visibility isn't purely driven
      // by --pdp-reveal-progress) can render mid-transition for a moment while
      // progress is frozen/being recomputed across the lock+restore window — visible
      // as content popping in/out right as the popup opens or closes, itself reading
      // as the page "moving". Hide it outright for the whole lock+settle window;
      // section-main-product.css defines the actual display:none.
      infoWrapper.classList.add('pdp-kiwi-lock-hide-swatches');
    };
    window._parallaxResumePanelReveal = function () {
      panelTrigger.enable();
      // NOT .refresh() — refreshing this trigger re-invokes its function-based `end`
      // ("top top+=N", resolved against infoWrapper's live position), and to do that
      // safely GSAP briefly reverts the scrub to progress 0 before remeasuring. Since
      // progress 0 collapses this panel's content (that's the whole point of the
      // reveal), that revert is exactly the "flickers down to top" glitch on close.
      var progress = pausedProgress !== null ? pausedProgress : panelTrigger.progress;
      panelTrigger.progress = progress;
      // With scrub:0.1 the tween that actually drives --pdp-reveal-progress doesn't
      // jump to match self.progress, it eases toward it over ~0.1s via GSAP's own
      // scrub-smoothing tween — force the tween's own playhead directly (bypasses that
      // easing, renders synchronously) so there's nothing left to animate. Normal
      // scroll-driven scrub smoothing resumes on the next real scroll tick as usual.
      tween.progress(progress);
      pausedProgress = null;
      updateHeaderVisibility(panelTrigger);
      updateFullyExpandedClass();
      // Scroll/progress are fully settled at this point (see settleAfterKiwiClose /
      // unlockPdpSidebarPageScroll, both call this last) — safe to reveal again.
      infoWrapper.classList.remove('pdp-kiwi-lock-hide-swatches');
    };

    // Header sync — one continuous, idempotent check re-evaluated on every scroll tick,
    // not a pair of imperative freeze()/thaw() calls that have to be started and stopped
    // correctly on every code path. It cannot desync because history doesn't matter:
    // whatever the current position is fully determines the answer.
    //   progress <= 0  → hasn't started approaching yet: normal header behavior.
    //   progress > 0   → mid-approach, reveal, or fully docked: force visible, for the
    //     rest of the scroll (progress saturates at 1 once docked and never drops back
    //     down except by scrolling back up above the panel entirely) — the header stays
    //     shown for as long as the panel is expanded, however far you scroll into its
    //     content below.
    var headerForcedVisible = false;
var panelWasFullyOpen = false;
var lastScrollY = window.scrollY;

// Header can hide only after scrolling 50px down AFTER panel is fully open.
var HEADER_HIDE_DELAY_PX = 50;
var headerFullyOpenScrollY = null;

    function updateHeaderVisibility(self) {
      if (isKiwiModalOpen() || isPdpSidebarOpen()) return;

      var hEl = document.querySelector('sticky-header');
      if (!hEl) return;

      var st = self && typeof self.progress === 'number' ? self : panelTrigger;
      if (!st) return;

      var progress = st.progress;
      var currentScrollY = window.scrollY;
      var scrollingDown = currentScrollY > lastScrollY;
      var scrollingUp = currentScrollY < lastScrollY;

      /*
       * PANEL IS OPENING
       * Keep header visible and prevent Shopify from hiding it.
       */
      if (progress > 0 && progress < 1) {
        hEl.preventHide = true;
        hEl.preventReveal = true;

        if (hEl.header) {
          hEl.header.classList.remove('shopify-section-header-hidden');
          hEl.header.classList.add('shopify-section-header-sticky');
        }

        if (!headerForcedVisible && typeof hEl.reveal === 'function') {
          hEl.reveal();
        }

        headerForcedVisible = true;
      }

      /*
       * PANEL IS FULLY OPEN
       *
       * Do NOT immediately allow Shopify to hide the header on the
       * same scroll event that reached progress = 1.
       */
      else if (progress >= 1) {
  if (!panelWasFullyOpen) {
    // Panel has just become fully open.
    // Keep header visible and start the 50px hide threshold.
    hEl.preventHide = true;
    hEl.preventReveal = true;

    headerFullyOpenScrollY = currentScrollY;

    if (hEl.header) {
      hEl.header.classList.remove('shopify-section-header-hidden');
      hEl.header.classList.add('shopify-section-header-sticky');
    }

    if (typeof hEl.reveal === 'function') {
      hEl.reveal();
    }

    panelWasFullyOpen = true;
  } else {
    // Panel is already fully open.
    var scrollDownDistance = currentScrollY - headerFullyOpenScrollY;

    if (scrollingDown && scrollDownDistance >= HEADER_HIDE_DELAY_PX) {
      // Only now allow Shopify to hide the header.
      hEl.preventHide = false;
      hEl.preventReveal = false;
    } else if (scrollingUp) {
      // Scrolling back up should reveal the header normally.
      hEl.preventHide = false;
      hEl.preventReveal = false;

      if (hEl.header) {
        hEl.header.classList.remove('shopify-section-header-hidden');
        hEl.header.classList.add('shopify-section-header-sticky');
      }

      if (typeof hEl.reveal === 'function') {
        hEl.reveal();
      }
    } else {
      // Still inside the first 50px after opening.
      // Keep header visible.
      hEl.preventHide = true;
      hEl.preventReveal = true;

      if (hEl.header) {
        hEl.header.classList.remove('shopify-section-header-hidden');
        hEl.header.classList.add('shopify-section-header-sticky');
      }
    }
  }

  headerForcedVisible = false;
}

      /*
       * PANEL IS CLOSED
       */
      else {
        panelWasFullyOpen = false;
        headerFullyOpenScrollY = null;
              
        hEl.preventHide = false;
        hEl.preventReveal = false;

        headerForcedVisible = false;
      }

      lastScrollY = currentScrollY;
    }

    // max-height:0 alone can still leave a sliver of layout space (padding/border on a
    // button aren't necessarily clipped to nothing depending on box-sizing), and display
    // can't be driven by a continuous CSS var anyway (calc()/var() don't apply to it) —
    // needs a discrete class, added only once truly settled at progress 1 (debounced,
    // not on every scrub tick, for the same reason the collapsed-side version of this
    // avoids rapid re-toggling right at the settle boundary).
    function updateFullyExpandedClass() {
      infoWrapper.classList.toggle('pdp-fully-expanded', panelTrigger.progress >= 1);
    }

    // ── Auto-snap to nearest state once the interaction ends ──────────────────────
    // Native scroll follows the finger 1:1 while touching, so "the gesture ended" is
    // touchend/touchcancel — but momentum can keep the page moving after that, so we
    // also wait for the scroll position itself to go quiet (no scroll events for
    // SETTLE_MS) before treating it as settled. Whichever happens last is what starts
    // the snap. isAutoSnapping guards against our own smooth-scroll call re-triggering
    // this same logic as it fires its own scroll events.
    var SETTLE_MS = 120;
    var TOUCH_WATCHDOG_MS = 3000;
    var isTouchingPanel = false;
    var isAutoSnapping = false;
    var settleTimer = null;
    var touchWatchdogTimer = null;
    // Progress when the current gesture started. Snap direction is based on this, not on
    // which state is numerically nearest — "nearest" was the bug: swipe up but release
    // before crossing the 50% mark, and progress<0.5 sent it back to collapse, opposite
    // of the swipe.
    var gestureStartProgress = 0;
    // Set once the bar-style swipe hijack (below) fires its instant jump, so any
    // trailing native scroll/momentum from the same physical gesture — or a
    // rubber-band snap-back after it — can't run auto-snap again and undo the jump.
    // Cleared on the next fresh touchstart, so a genuine new swipe (e.g. an
    // intentional downward swipe to collapse) is never affected.
    var ignoreAutoSnapUntilNextTouch = false;

    // TEMP DEBUG — remove once auto-snap is confirmed working on device.
    function dbg() {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[pdp-snap]');
      console.log.apply(console, args);
    }

    // iOS Safari can silently ignore/drop `scrollTo({behavior:'smooth'})` when it's
    // called right after our own preventDefault-heavy touch handling (residual
    // momentum/scroll-anchoring fights the native smooth-scroll animation, and it
    // just never starts) — scrollY then never reaches `target`, and the old
    // poll-for-native-smooth-scroll approach could sit for the length of its
    // watchdog with the panel visibly stuck mid-drag ("close doesn't happen").
    // Driving the animation ourselves with rAF removes that dependency entirely —
    // it always runs, regardless of what WebKit's own smooth-scroll does.
    var SNAP_DURATION_MS = 300;
    var arriveGeneration = 0;
    function easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }
    function animateScrollTo(target) {
  var generation = ++arriveGeneration;
  var startY = window.scrollY;
  var distance = target - startY;

  if (parallaxRoot) parallaxRoot.classList.add('is-snapping');

  // Only when closing the panel, final document scroll must be 0.
  var isClosing = target <= panelTrigger.start + 1;
  var finalTarget = isClosing ? 0 : target;

  function finish() {
    if (generation !== arriveGeneration) return;

    if (isClosing) {
      // iOS Safari: force document/body scroll position to exactly 0.
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } else {
      window.scrollTo(0, finalTarget);
    }

    isAutoSnapping = false;

    if (parallaxRoot) {
      parallaxRoot.classList.remove('is-snapping');
    }

    updateFullyExpandedClass();

    if (typeof window._pdpVhSync === 'function') {
      window._pdpVhSync();
    }

    panelTrigger.update();

    // iOS Safari can silently restore the pre-gesture scroll position a frame
    // or two after touchend, right after the eased close animation has already
    // finished — re-assert 0 on the next couple frames so that never undoes the
    // just-completed smooth close. Purely corrective (values already are 0 in
    // the normal case), so it never fights the animation itself.
    if (isClosing) {
      var reassertGeneration = generation;
      // 2 frames wasn't always enough on real iOS Safari — residual momentum/
      // rubber-band from the same physical gesture can keep nudging scrollY
      // for longer than that after touchend, past the point this used to stop
      // re-asserting. Keep correcting for a handful of frames so nothing wins
      // the race after we've stopped checking.
      var reassertFramesLeft = 8;
      function reassertZero() {
        if (reassertGeneration !== arriveGeneration) return;
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        panelTrigger.update();
        reassertFramesLeft--;
        if (reassertFramesLeft > 0) {
          requestAnimationFrame(reassertZero);
        }
      }
      requestAnimationFrame(reassertZero);
    }
  }

  if (Math.abs(distance) < 1) {
    finish();
    return;
  }

  var startTime = null;

  function step(now) {
    if (generation !== arriveGeneration) return;

    if (startTime === null) startTime = now;

    var elapsed = now - startTime;
    var t = Math.min(elapsed / SNAP_DURATION_MS, 1);

    var nextY = startY + distance * easeOutCubic(t);

    // Do not allow close animation to go below 0.
    if (isClosing) {
      nextY = Math.max(0, nextY);
    }

    // iOS Safari quantizes scroll position to whole pixels internally — feeding it
    // a sub-pixel float every frame makes it re-snap each time, reading as
    // micro-stutter over the course of the animation. Round before writing.
    window.scrollTo(0, Math.round(nextY));

    if (t >= 1) {
      finish();
      return;
    }

    requestAnimationFrame(step);
  }

  requestAnimationFrame(step);
}
    // Kept as the external name other call sites already use.
    function waitForArrivalThenClear(target) {
      animateScrollTo(target);
    }

    function autoSnapToNearest() {
      if (ignoreAutoSnapUntilNextTouch) {
        dbg('autoSnapToNearest: skipped, ignoreAutoSnapUntilNextTouch');
        return;
      }
      if (isAutoSnapping || isTouchingPanel) {
        dbg('autoSnapToNearest: skipped, isAutoSnapping=', isAutoSnapping, 'isTouchingPanel=', isTouchingPanel);
        return;
      }
      if (isPdpSidebarOpen() || isKiwiModalOpen()) {
        dbg('autoSnapToNearest: skipped, sidebar/kiwi open');
        return;
      }
      var progress = panelTrigger.progress;
      if (progress <= 0 || progress >= 1) {
        infoWrapper.querySelector('product-form').style.opacity = 1;
        dbg('autoSnapToNearest: skipped, already settled, progress=', progress);
        return;
      }
      var expand = progress === gestureStartProgress ? progress >= 0.5 : progress > gestureStartProgress;
      var target = expand ? panelTrigger.end : panelTrigger.start;
      dbg('autoSnapToNearest: SNAPPING, progress=', progress, 'startProgress=', gestureStartProgress, 'expand=', expand);
      isAutoSnapping = true;
      waitForArrivalThenClear(target);
    }

    function scheduleSettleCheck() {
      dbg('scheduleSettleCheck: timer (re)armed');
      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        infoWrapper.querySelector('product-form').style.opacity = 1;
        dbg('settle timer fired, isTouchingPanel=', isTouchingPanel);
        if (isTouchingPanel) return;
        autoSnapToNearest();
        updateFullyExpandedClass();
      }, SETTLE_MS);
    }

    // Capture phase, not bubble — Swiper (the media gallery) and other touch-driven
    // widgets on this page commonly call stopPropagation() on their own touch handling,
    // which would stop a bubble-phase listener here from ever seeing touchend and leave
    // isTouchingPanel stuck true forever (blocking every future auto-snap). Capture fires
    // top-down before any descendant gets a chance to stop propagation, so it can't be
    // swallowed that way.
    function clearTouching() {
      dbg('clearTouching (touchend/touchcancel/watchdog)');
      isTouchingPanel = false;
      clearTimeout(touchWatchdogTimer);
      // Decide + snap right on release instead of routing through
      // scheduleSettleCheck's SETTLE_MS "wait for scroll to go quiet" debounce. That
      // debounce was the source of the two-stage look: native momentum coasts the
      // panel to a stop on its own first (reads as "moves to middle, pauses"), and
      // only once scroll events go fully quiet does the programmatic smooth-scroll
      // snap start — a visible dead pause between drag and expansion. panelTrigger.
      // progress already reflects the true release position immediately (the scrub
      // lag only smooths the CSS reveal var, not this), so the expand/collapse
      // direction can be decided the instant the finger lifts and the snap animation
      // can continue straight on from wherever the panel is, with no intermediate
      // stop.
      clearTimeout(settleTimer);
      var productForm = infoWrapper.querySelector('product-form');
      if (productForm) productForm.style.opacity = 1;
      autoSnapToNearest();
      updateFullyExpandedClass();
    }
    window.addEventListener('touchstart', function () {
      dbg('touchstart');
      isTouchingPanel = true;
      gestureStartProgress = panelTrigger.progress;
      ignoreAutoSnapUntilNextTouch = false;
      clearTimeout(settleTimer);
      // Belt-and-suspenders: if touchend/touchcancel never arrives for some unforeseen
      // reason, don't let isTouchingPanel stay stuck true forever — that would silently
      // disable auto-snap for the rest of the page's lifetime.
      clearTimeout(touchWatchdogTimer);
      touchWatchdogTimer = setTimeout(clearTouching, TOUCH_WATCHDOG_MS);
    }, { passive: true, capture: true });
    window.addEventListener('touchend', clearTouching, { passive: true, capture: true });
    window.addEventListener('touchcancel', clearTouching, { passive: true, capture: true });
    window.addEventListener('scroll', function () {
      if (typeof window.sizePeekClose === 'function') window.sizePeekClose();
      if (isAutoSnapping) return;
      dbg('scroll event, progress=', panelTrigger.progress);
      scheduleSettleCheck();
    }, { passive: true });

    // Last-resort watchdog — independent of animateScrollTo/verifySettled and their
    // generation tracking, so it catches this no matter which code path left it
    // stuck. iOS Safari can leave scrollY a few px off panelTrigger.start (0) after
    // a collapse — including a drag released partway through, before it even
    // crosses the commit threshold — past every other correction attempt in this
    // file. Runs on a plain interval, only ever nudges scrollY when the panel is
    // fully at rest (never mid-drag/mid-snap/mid-touch) so it can't fight a live
    // gesture.
    setInterval(function () {
      if (isTouchingPanel || isAutoSnapping || barDrag || barSwipeGesture) return;
      if (isPdpSidebarOpen() || isKiwiModalOpen()) return;
      // A stuck-nonzero scrollY on iOS (rubber-band/momentum restoring a few px
      // after touchend) makes ScrollTrigger's own progress read as a tiny
      // nonzero value instead of 0 — since progress is DERIVED from scrollY,
      // gating this watchdog on "progress > 0" means the exact case it exists
      // to fix (scrollY stuck a few px off start) can also suppress it. Gate on
      // actual pixel distance from the collapsed anchor instead.
      if (Math.abs(window.scrollY - panelTrigger.start) > 12) return; // genuinely mid-transition, leave it alone
      if (window.scrollY !== panelTrigger.start) {
        window.scrollTo(0, panelTrigger.start);
        document.documentElement.scrollTop = panelTrigger.start;
        document.body.scrollTop = panelTrigger.start;
        panelTrigger.update();
      }
    }, 250);

    function toggleExpanded() {
      if (!isMobile()) return;
      if (isPdpSidebarOpen() || isKiwiModalOpen()) return;
      var atStart = panelTrigger.progress < 0.5;
      var target = atStart ? panelTrigger.end : panelTrigger.start;
      // Same guard the auto-snap uses — this scrollTo's own scroll events shouldn't
      // be mistaken for a fresh user gesture that needs settling/snapping.
      isAutoSnapping = true;
      waitForArrivalThenClear(target);
    }

    // Tap the handle — jump straight to fully collapsed or fully expanded.
    if (bar) {
      bar.addEventListener('click', toggleExpanded);
    }

    // ── Drag the handle directly → real finger-follow, 50px release threshold ──
    // Unlike the swipe-hijack below (which jumps instantly once intent is clear),
    // dragging the bar itself must visually follow the finger the whole time, and
    // only decide open-vs-close once the finger lifts: >50px dragged toward a
    // direction (either way) commits to that state; anything less snaps back to
    // whatever state the gesture started from. The bar is excluded from the
    // generic infoWrapper swipe handler above (BAR_SWIPE_EXCLUDED_SELECTORS) so
    // the two gestures never fight over the same touch.
    //
    // Distance is measured from finger position (lastY - startY), NOT from
    // window.scrollY at release — iOS Safari can lag/rubber-band scrollY commits
    // a frame or two behind the actual touch, so reading scrollY right at
    // touchend under-reports the drag and the threshold silently misses.
    var BAR_DRAG_THRESHOLD_PX = 80;
    var barDrag = null; // {startY, lastY, startScrollY, startProgress, moved}

    if (bar) {
      bar.addEventListener('touchstart', function (e) {
        if (isPdpSidebarOpen() || isKiwiModalOpen()) return;
        var t = e.touches[0];
        barDrag = {
          startY: t.clientY,
          lastY: t.clientY,
          startScrollY: window.scrollY,
          startProgress: panelTrigger.progress,
          moved: false,
        };
        if (parallaxRoot) parallaxRoot.classList.add('is-dragging');
        // Stop the generic scroll listener/auto-snap from reacting to our own
        // scrollTo calls below as if they were a fresh free-scroll gesture.
        isAutoSnapping = true;
        clearTimeout(settleTimer);
        if (e.cancelable) e.preventDefault();
      }, { passive: false, capture: true });

      bar.addEventListener('touchmove', function (e) {
        if (!barDrag) return;
        barDrag.moved = true;
        var t = e.touches[0];
        barDrag.lastY = t.clientY;
        var deltaY = barDrag.startY - t.clientY; // positive = dragged up (toward open)
        var targetScrollY = barDrag.startScrollY + deltaY;
        // Clamp to the panel's own collapsed/expanded scroll bounds — dragging
        // past either end shouldn't scroll the rest of the page.
        var minY = Math.min(panelTrigger.start, panelTrigger.end);
        var maxY = Math.max(panelTrigger.start, panelTrigger.end);
        if (targetScrollY < minY) targetScrollY = minY;
        if (targetScrollY > maxY) targetScrollY = maxY;
        window.scrollTo(0, targetScrollY);
        panelTrigger.update();
        updateHeaderVisibility(panelTrigger);
        if (e.cancelable) e.preventDefault();
        if (e.stopPropagation) e.stopPropagation();
      }, { passive: false, capture: true });

      function endBarDrag() {
        if (!barDrag) return;
        var drag = barDrag;
        barDrag = null;
        if (parallaxRoot) parallaxRoot.classList.remove('is-dragging');
        if (!drag.moved) {
          toggleExpanded();
          return;
        }
        var draggedUpPx = drag.startY - drag.lastY; // positive = dragged toward expanded
        var wasExpanded = drag.startProgress >= 0.5;
        var committedOpen = !wasExpanded && draggedUpPx > BAR_DRAG_THRESHOLD_PX;
        var committedClosed = wasExpanded && -draggedUpPx > BAR_DRAG_THRESHOLD_PX;
        var target;
        if (committedOpen) {
          target = panelTrigger.end;
        } else if (committedClosed) {
          target = panelTrigger.start;
        } else {
          // Under threshold — revert to whatever state the gesture started from.
          target = wasExpanded ? panelTrigger.end : panelTrigger.start;
        }
        isAutoSnapping = true;
        waitForArrivalThenClear(target);
        updateFullyExpandedClass();
      }
      bar.addEventListener('touchend', endBarDrag, { passive: true, capture: true });
      bar.addEventListener('touchcancel', endBarDrag, { passive: true, capture: true });
    }

    // ── Drag anywhere on the panel body → same finger-follow + 100px threshold ──
    // as dragging the bar handle itself (see BAR_DRAG_THRESHOLD_PX above): the
    // panel follows the finger live, and only commits to open/closed once the
    // finger lifts, based on total px dragged — not an instant jump partway
    // through the gesture. Tap/click on buttons, links, variant pills etc. must
    // keep working exactly as before — see the clickable-target handling below.
    var SWIPE_INTENT_PX = 15;
    var barSwipeGesture = null; // {startX, startY, hijacked, defaultBlocked, startScrollY, startProgress}

    // Sub-widgets with their own genuine internal gesture needs (the open-accordion
    // scroll-chaining fix, the sidebar/loyalty/app-block embeds) stay out of this
    // entirely — everything else, including plain description text and every
    // interactive control, is fair game for the drag now, since click/tap/text
    // selection on them is preserved separately below.
    var BAR_SWIPE_EXCLUDED_SELECTORS = [
      '.product-form__select-size-warning',
      '.usp-bar-block',
      '.product__accordion',
      '.pdp-sidebar-trigger',
      '.installment',
      '.loyalty-points-for-product',
      '.shopify-block.shopify-app-block',
      '.product-parallax-bar',
      '.product__title-swatches',
    ].join(',');

    function isBarSwipeExcluded(target) {
      return !!(target.closest && target.closest(BAR_SWIPE_EXCLUDED_SELECTORS));
    }

    // Elements with their own native tap/click behavior (buttons, links, radio/label
    // pills for variant selection, form inputs) — preventDefault on touchstart would
    // silently cancel their click, so for these the default is instead blocked on
    // the first touchmove (see below): still early enough to stop native scroll
    // before real momentum builds, but late enough that a plain tap (no touchmove
    // ever fires) reaches its click handler completely untouched.
    function isClickableTarget(target) {
      return !!(target.closest && target.closest('a, button, input, select, textarea, label, [role="button"]'));
    }

    infoWrapper.addEventListener('touchstart', function (e) {
      if (isPdpSidebarOpen() || isKiwiModalOpen() || isBarSwipeExcluded(e.target)) {
        barSwipeGesture = null;
        return;
      }
      var clickable = isClickableTarget(e.target);
      // Already sitting at a boundary (fully expanded or fully collapsed) — the
      // swipe direction isn't known yet, and if it turns out to be the "no further
      // to go" direction (e.g. swipe up while already fully expanded), native
      // scroll needs to be free to handle it (scrolling through content below the
      // docked panel). Blocking default here unconditionally, like the mid-
      // transition case does, would kill that scroll before touchmove ever gets a
      // chance to tell direction apart — defer the decision to touchmove instead,
      // same as clickable targets already do.
      var atBoundary = panelTrigger.progress >= 1 || panelTrigger.progress <= 0;
      // Non-interactive zone (title/price text, etc.) mid-transition: block the
      // browser's default scroll/momentum for this gesture from pixel zero.
      // Waiting until a swipe is confirmed on touchmove lets the first few px of
      // movement register as a real native scroll/fling on the compositor thread
      // (Chrome's fast-path scrolling, iOS's UIScrollView velocity capture) —
      // preventDefault on later touchmove events can't retroactively cancel
      // momentum that already started, so once the finger lifts that leftover
      // fling keeps playing under our jump/hold, eventually dragging the panel
      // back down on its own with plain native scroll. Preventing default here, at
      // touchstart, means no native scroll pipeline is ever entered for this
      // gesture in the first place. Clickable targets and boundary starts can't
      // take this fast path — see touchmove.
      if (!clickable && !atBoundary && e.cancelable) e.preventDefault();
      var t = e.touches[0];
      barSwipeGesture = {
        startX: t.clientX,
        startY: t.clientY,
        lastY: t.clientY,
        hijacked: false,
        defaultBlocked: !clickable && !atBoundary,
        startScrollY: window.scrollY,
        startProgress: panelTrigger.progress,
      };
    }, { passive: false, capture: true });

    infoWrapper.addEventListener('touchmove', function (e) {
      if (!barSwipeGesture) return;
      var t = e.touches[0];
      barSwipeGesture.lastY = t.clientY;
      var deltaY = barSwipeGesture.startY - t.clientY; // positive = dragged up (toward open)
      var deltaX = t.clientX - barSwipeGesture.startX;
      if (barSwipeGesture.hijacked) {
        // Gesture already converted to a live drag — follow the finger for the
        // rest of this touch, same as the bar-drag handler above.
        var minY = Math.min(panelTrigger.start, panelTrigger.end);
        var maxY = Math.max(panelTrigger.start, panelTrigger.end);
        var targetScrollY = barSwipeGesture.startScrollY + deltaY;
        if (targetScrollY < minY) targetScrollY = minY;
        if (targetScrollY > maxY) targetScrollY = maxY;
        window.scrollTo(0, targetScrollY);
        panelTrigger.update();
        updateHeaderVisibility(panelTrigger);
        if (e.cancelable) e.preventDefault();
        if (e.stopPropagation) e.stopPropagation();
        return;
      }
      // Direction already exhausted (fully expanded and still swiping up, or
      // fully collapsed and still swiping down) — there's nothing for the drag
      // to do. Drop the gesture without touching default so native scroll (e.g.
      // scrolling through content below the docked panel) takes over normally for
      // the rest of this touch, instead of leaving it dead (see touchstart's
      // atBoundary deferral above — this is the check that deferral was waiting
      // on).
      var expand = deltaY > 0;
      var progress = panelTrigger.progress;
      if ((expand && progress >= 1) || (!expand && progress <= 0)) {
        barSwipeGesture = null;
        return;
      }
      if (!barSwipeGesture.defaultBlocked) {
        // Gesture started on a clickable control (or at a boundary) — this is the
        // first touchmove, meaning real movement is happening, so it's no longer a
        // plain tap (a tap never fires touchmove at all, so its click is
        // unaffected by this). Block native scroll now, before any of it has
        // actually happened yet.
        if (e.cancelable) e.preventDefault();
        barSwipeGesture.defaultBlocked = true;
      }
      if (Math.abs(deltaY) < SWIPE_INTENT_PX || Math.abs(deltaY) < Math.abs(deltaX) * 1.5) return;
      barSwipeGesture.hijacked = true;
      if (parallaxRoot) parallaxRoot.classList.add('is-dragging');
      // This same physical gesture can keep generating scroll/momentum/rubber-band
      // events after the drag below finishes — none of those are a new user
      // decision, so auto-snap must ignore all of it until a genuinely new touch
      // starts (see ignoreAutoSnapUntilNextTouch above).
      ignoreAutoSnapUntilNextTouch = true;
      isAutoSnapping = true;
      clearTimeout(settleTimer);
      if (e.cancelable) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
    }, { passive: false, capture: true });

    function endInfoWrapperDrag() {
  if (!barSwipeGesture) return;

  var gesture = barSwipeGesture;
  barSwipeGesture = null;

  // A fast flick can lift the finger before the 15px/angle hijack check in
  // touchmove ever confirms intent — gesture.hijacked stays false even though
  // this was a real close swipe. Previously that meant returning here and
  // leaving it to whatever native scroll/momentum was already in flight,
  // which finishes the close instantly with no easing at all (the "bar drag
  // is smooth but body drag just jumps" symptom). Fall back to the same
  // progress-based decision autoSnapToNearest uses, so a fast flick still
  // resolves through the eased animation instead of raw native scroll.
  if (!gesture.hijacked) {
    if (parallaxRoot) parallaxRoot.classList.remove('is-dragging');
    autoSnapToNearest();
    // autoSnapToNearest() treats panelTrigger.progress <= 0 as "already
    // settled" and does nothing — but on a fast flick, native momentum can
    // have stopped a few px short of true 0 by the time progress reads as
    // collapsed, leaving scrollY not actually at rest. Land it exactly, same
    // as animateScrollTo's own isClosing handling does.
    if (panelTrigger.progress <= 0 && window.scrollY !== panelTrigger.start) {
      window.scrollTo(0, panelTrigger.start);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      panelTrigger.update();
    }
    updateFullyExpandedClass();
    return;
  }

  if (parallaxRoot) {
    parallaxRoot.classList.remove('is-dragging');
  }

  var draggedUpPx = gesture.startY - gesture.lastY;
  var wasExpanded = gesture.startProgress >= 0.5;

  var committedOpen =
    !wasExpanded &&
    draggedUpPx > BAR_DRAG_THRESHOLD_PX;

  var committedClosed =
    wasExpanded &&
    -draggedUpPx > BAR_DRAG_THRESHOLD_PX;

  var target;

  if (committedOpen) {
    target = panelTrigger.end;
  } else if (committedClosed) {
    target = panelTrigger.start;
  } else {
    target = wasExpanded
      ? panelTrigger.end
      : panelTrigger.start;
  }

  isAutoSnapping = true;

  // Route through the same eased rAF animation as expand and as the bar-handle
  // drag (endBarDrag below) — animateScrollTo already forces scrollTop to
  // exactly 0 on arrival for iOS Safari when closing (see isClosing handling
  // in finish()). This used to short-circuit straight to an instant
  // multi-rAF scrollTo(0,0) here, skipping the easing entirely, which is why
  // body-drag close looked abrupt next to the bar's smooth glide.
  waitForArrivalThenClear(target);

  updateFullyExpandedClass();
}
    infoWrapper.addEventListener('touchend', endInfoWrapperDrag, { passive: true, capture: true });
    infoWrapper.addEventListener('touchcancel', endInfoWrapperDrag, { passive: true, capture: true });

    // Consumed by product-swatches.liquid when a swatch tap should reveal the full
    // PDP on a page that's already loaded (no navigation happening).
    window.productParallaxBarToggle = toggleExpanded;

    // Consumed by media-gallery.js: swiping up while already at the gallery's last slide
    // (nowhere further for the gallery itself to go) expands the panel — collapse-only
    // gestures elsewhere stay untouched, this is expand-only and a no-op if already open.
    window.__pdpExpandPanel = function () {
      if (!isMobile()) return;
      if (isPdpSidebarOpen() || isKiwiModalOpen()) return;
      if (panelTrigger.progress >= 1) return;
      isAutoSnapping = true;
      waitForArrivalThenClear(panelTrigger.end);
    };

    // Re-measure on viewport/layout changes that shift the panel's top position (start/end
    // are based on infoWrapper's own top edge, not its height, so its own content growing/
    // shrinking doesn't move them — but header height, gallery height, and orientation do).
    // Not on every resize tick (ignoreMobileResize above already absorbs the iOS
    // toolbar-driven ones).
    var refreshTimer = null;
    function scheduleRefresh() {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(function () {
        // body is position:fixed for the whole Kiwi/sidebar lock window — scrollY reads
        // 0 and every element's getBoundingClientRect() reflects that frozen visual
        // snapshot, not real document layout. A refresh landing here permanently bakes
        // this trigger's cached start/end off that bogus geometry (nothing looks wrong
        // until the lock releases and resume recomputes progress against them — the
        // close-only jump this was causing). font/variant/orientation refreshes can
        // land here purely on timing (e.g. slow font load on a given device) — retry
        // instead of refreshing until the lock actually clears.
        if (isPdpSidebarOpen() || isKiwiModalOpen()) {
          scheduleRefresh();
          return;
        }
        ScrollTrigger.refresh();
      }, 150);
    }
    document.addEventListener('variant:change', scheduleRefresh);
    document.addEventListener('product:size-system-change', scheduleRefresh);
    window.addEventListener('orientationchange', scheduleRefresh);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleRefresh);
    }
    // Accordion open/close changes content height too.
    document.addEventListener('toggle', function (e) {
      if (e.target && e.target.closest && e.target.closest('.product__accordion')) {
        scheduleRefresh();
      }
    }, true);

    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
      // Width-only changes are real (orientation/responsive) — height-only changes are
      // the iOS toolbar, already absorbed by ScrollTrigger.config({ignoreMobileResize}).
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      scheduleRefresh();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/**
 * Hide the fixed gallery when the product section is out of view (scrolled past or above).
 * Sections below cover it via z-index, but we hide it when the product section itself
 * is not in the viewport at all to avoid ghost rendering.
 */
(function () {
  function isMobile() {
    return window.innerWidth <= 1023;
  }

  function updateGalleryVisibility() {
    // Skip while a gesture/animation owns the scroll — this forces a
    // getBoundingClientRect() on `.product-paralax` (a large ancestor, expensive) on
    // every scroll dispatch, unguarded, unlike the file's main scroll listener which
    // already skips itself during drag for exactly this reason. Since window.scrollTo()
    // inside the drag's rAF loop fires a native scroll event every frame, this was
    // forcing a synchronous layout on every single frame of every drag — direct,
    // fixable contributor to drag feeling laggy compared to native scroll (which never
    // triggers this path with the same frequency/no coordinated per-frame cost).
    if (typeof window._pdpParallaxIsBusy === 'function' && window._pdpParallaxIsBusy()) return;
    var root = document.querySelector('.product-paralax');
    var mediaWrapper = root && root.querySelector('.product__media-wrapper');
    if (!root || !mediaWrapper) return;

    if (!isMobile()) {
      mediaWrapper.style.visibility = '';
      return;
    }

    var rootRect = root.getBoundingClientRect();
    // Hide when section is fully above viewport (user scrolled back up past product)
    if (rootRect.bottom <= 0) {
      mediaWrapper.style.visibility = 'hidden';
    } else {
      mediaWrapper.style.visibility = '';
    }
  }

  function init() {
    if (!document.querySelector('.product-paralax')) return;
    window.addEventListener('scroll', updateGalleryVisibility, { passive: true });
    window.addEventListener('resize', updateGalleryVisibility, { passive: true });
    updateGalleryVisibility();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Freeze the viewport height on iOS to prevent toolbar show/hide from causing
 * layout shifts on dvh-based gallery sizing (the main source of PDP flicker on iPhone).
 *
 * iOS Safari's dynamic toolbar changes window.innerHeight (and 100dvh) every time
 * the toolbar shows or hides during scroll. Every element sized with dvh recalculates,
 * forcing a full layout + repaint = the visible flicker.
 *
 * Fix: set --pdp-vh once to the initial innerHeight, then only update it on genuine
 * viewport resize (orientation change) — never on toolbar show/hide.
 * The gallery CSS uses calc(var(--pdp-vh, 1svh) * 100) instead of 100dvh.
 *
 * We detect "real resize" vs "toolbar resize" by checking if innerWidth changed.
 * The toolbar shows/hides change only innerHeight; orientation changes change both.
 */
(function () {
  if (typeof window === 'undefined') return;

  var lastSetWidth = 0;
  var lastSetHeight = 0;
  var resizeTimer = null;
  // Whether the toolbar is currently visible (scrollY near 0).
  // We update --pdp-vh when the toolbar settles, not during scroll transitions.
  var toolbarSettleTimer = null;

  function setPdpVh(forceHeight) {
    var ih = typeof forceHeight === 'number' ? forceHeight : window.innerHeight;
    var iw = window.innerWidth;
    // Skip if nothing meaningful changed
    if (lastSetWidth !== 0 && iw === lastSetWidth && Math.abs(ih - lastSetHeight) < 3) {
      return;
    }
    lastSetWidth = iw;
    lastSetHeight = ih;
    document.documentElement.style.setProperty('--pdp-vh', ih * 0.01 + 'px');
  }

  function onResize() {
    var iw = window.innerWidth;
    var ih = window.innerHeight;
    // Real resize: width changed (orientation change) — update immediately after debounce.
    if (lastSetWidth !== 0 && iw !== lastSetWidth) {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { setPdpVh(); }, 150);
      return;
    }
    // Height-only change = toolbar show/hide.
    // If toolbar is showing (height got smaller), update --pdp-vh after a short settle
    // so layout matches the smaller viewport. Without this, snap targets are computed
    // against a frozen-tall gallery that extends beyond getMaxScrollTop().
    // Use a 400ms delay so we don't thrash during the toolbar animation itself.
    clearTimeout(toolbarSettleTimer);
    toolbarSettleTimer = setTimeout(function () {
      toolbarSettleTimer = null;
      // Only update if no snap/drag is in progress — avoid layout shift mid-animation.
      var isAnimating = document.querySelector('.product-paralax.is-snapping, .product-paralax.is-dragging');
      if (isAnimating) return;
      setPdpVh();
    }, 400);
  }

  // Expose so snap animations can trigger a sync after they complete
  window._pdpVhSync = function () {
    clearTimeout(toolbarSettleTimer);
    toolbarSettleTimer = null;
    setPdpVh();
  };

  function init() {
    if (!document.querySelector('.product-paralax')) return;
    setPdpVh();
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(setPdpVh, 200);
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Intercept direct Kiwi size-chart link clicks on PDP.
 * Kiwi sets body position:fixed which breaks position:sticky on the header.
 * Fix: switch .section-header to position:fixed while Kiwi modal is open.
 */
(function () {
  var KIWI_LINK_SEL = '.ks-chart-modal-link, .ks-chart-modal-button, .ks-calculator-modal-link, .ks-calculator-modal-button';
  function isMobileViewport() {
    return window.innerWidth <= 1023;
  }
  function getHeaderHeightKiwi() {
    // Read from the CSS variable first — reliable even when the header is
    // hidden/sticky/animated (offsetHeight returns 0 when off-screen).
    var hphPx = getComputedStyle(document.documentElement).getPropertyValue('--header-parent-height');
    if (hphPx && hphPx.trim()) {
      var parsed = parseFloat(hphPx);
      if (!isNaN(parsed) && parsed > 0) return Math.round(parsed);
    }
    // Fallback: try sticky-header offsetHeight (only reliable when header is visible)
    var h = document.querySelector('sticky-header');
    if (h && h.offsetHeight > 0) return h.offsetHeight;
    var hEl = document.querySelector('.shopify-section-header-wrapper, .section-header, header');
    if (hEl && hEl.offsetHeight > 0) return hEl.offsetHeight;
    // Last resort: --header-height variable
    return (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0', 10) || 0);
  }

  // The `scrollY - headerHeight` compensation below only makes sense when the lock
  // is ALSO about to reveal a header that wasn't already occupying that space (e.g.
  // it was scrolled/hidden out of view and preventHide forces it back). When
  // pinHeader() has already run (headerPinState.count > 0 — the expanded-panel
  // path, see prepareKiwiOpen) the header's flow space was already reserved and
  // visible BEFORE the lock, so subtracting headerHeight again double-compensates:
  // every locked-scroll element (including .product__info-wrapper, sticky on
  // --header-height) renders one header-height lower than it should — the gap
  // under the panel content that appears while the Kiwi/size-chart modal is open
  // and disappears on close (close restores scrollY via explicit scrollTo(),
  // bypassing this math entirely).
  function getKiwiScrollLockHeaderOffset() {
    return headerPinState.count > 0 ? 0 : getHeaderHeightKiwi();
  }

  // The panel's collapsed/expanded state is now a continuous scroll-progress value (see
  // product__info-pin's ScrollTrigger, id 'pdp-panel-reveal') rather than a discrete CSS
  // class — read it from ScrollTrigger's registry instead of a class that's no longer set.
  function isParallaxPanelCollapsed() {
    var st = typeof window.ScrollTrigger !== 'undefined' && window.ScrollTrigger.getById
      ? window.ScrollTrigger.getById('pdp-panel-reveal')
      : null;
    return st ? st.progress < 0.5 : true;
  }

  var kiwiScrollGuardState = window.__pdpKiwiScrollGuardState || (window.__pdpKiwiScrollGuardState = {
    active: false,
    scrollY: 0,
    hasSavedScroll: false,
    styles: null
  });

  function applyKiwiBodyScrollGuard(scrollY) {
    if (!document.body || typeof scrollY !== 'number') return;

    // Sanity-check scrollY against visualViewport.pageTop — if the bottom toolbar
    // appeared between touchstart and now, scrollY may be stale. visualViewport.pageTop
    // is the authoritative scroll position unaffected by scroll-anchoring on iOS.
    if (window.visualViewport && typeof window.visualViewport.pageTop === 'number') {
      var vpTop = Math.round(window.visualViewport.pageTop);
      // If the two values differ by more than 5px, prefer visualViewport
      // (scroll-anchor bumped scrollY but visualViewport stayed correct).
      if (Math.abs(vpTop - scrollY) > 5) {
        scrollY = vpTop;
      }
    }

    if (!kiwiScrollGuardState.active) {
      kiwiScrollGuardState.styles = {
        position: document.body.style.getPropertyValue('position'),
        top: document.body.style.getPropertyValue('top'),
        left: document.body.style.getPropertyValue('left'),
        right: document.body.style.getPropertyValue('right'),
        width: document.body.style.getPropertyValue('width')
      };
      kiwiScrollGuardState.active = true;
    }

    kiwiScrollGuardState.scrollY = scrollY;
    var headerHeight = getKiwiScrollLockHeaderOffset();
    var expectedTop = isMobileViewport() ? '-' + Math.round(Math.max(scrollY - headerHeight, 0)) + 'px' : '-' + Math.round(scrollY) + 'px';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.setProperty('position', 'fixed', 'important');
    document.body.style.setProperty('top', expectedTop, 'important');
    document.body.style.setProperty('left', '0', 'important');
    document.body.style.setProperty('right', '0', 'important');
    document.body.style.setProperty('width', '100%', 'important');
    document.body.style.setProperty('overflow', 'hidden', 'important');

    // Kiwi sets body { position:fixed; top:0 } synchronously and may continue
    // writing top:0 during its open animation. Watch body style for the entire
    // duration the modal is open and re-assert our top value every time Kiwi
    // overwrites it — preventing the background from scrolling to top.
    if (kiwiScrollGuardState._openGuardObs) {
      kiwiScrollGuardState._openGuardObs.disconnect();
      kiwiScrollGuardState._openGuardObs = null;
    }

    function getCorrectTop() {
      var hh = getKiwiScrollLockHeaderOffset();
      return isMobileViewport()
        ? '-' + Math.round(Math.max(scrollY - hh, 0)) + 'px'
        : '-' + Math.round(scrollY) + 'px';
    }

    var openGuard = new MutationObserver(function () {
      // If we are in the settling/close phase, stop guarding.
      if (window.__pdpKiwiSettling || !window.__pdpKiwiModalOpen) {
        openGuard.disconnect();
        kiwiScrollGuardState._openGuardObs = null;
        return;
      }
      var correctTop = getCorrectTop();
      // Re-assert position and top whenever Kiwi overwrites them.
      if (document.body.style.top !== correctTop || document.body.style.position !== 'fixed') {
        document.body.style.setProperty('top', correctTop, 'important');
        document.body.style.setProperty('position', 'fixed', 'important');
      }
      // Also keep html overflow hidden.
      if (document.documentElement.style.overflow !== 'hidden') {
        document.documentElement.style.overflow = 'hidden';
      }
    });
    openGuard.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
    openGuard.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
    kiwiScrollGuardState._openGuardObs = openGuard;
  }

  function clearKiwiBodyScrollGuard() {
    kiwiScrollGuardState.active = false;
    kiwiScrollGuardState.styles = null;
    if (kiwiScrollGuardState._openGuardObs) {
      kiwiScrollGuardState._openGuardObs.disconnect();
      kiwiScrollGuardState._openGuardObs = null;
    }
    releaseAllPageScrollLocks();
  }

  /** Strip Kiwi + our body/html locks so the page can scroll again after close. */
  function releaseAllPageScrollLocks() {
    if (typeof window.isPdpSidebarOpen === 'function' && window.isPdpSidebarOpen()) {
      return;
    }
    // Check if either size chart OR size peek is still open
    var sizePopupStillOpen = document.querySelector('.size-popup-wrapper.is-open');
    var kiwiModalStillOpen = document.body.classList.contains('kiwi-sizing-modal-visible') ||
      document.body.classList.contains('vanilla-modal-visible') ||
      document.documentElement.classList.contains('kiwi-sizing-modal-visible') ||
      document.documentElement.classList.contains('vanilla-modal-visible') ||
      !!document.querySelector('.kiwi-sizing-modal-visible, .vanilla-modal-visible');

    if (sizePopupStillOpen || kiwiModalStillOpen) {
      return;
    }

    // If an accordion drawer is open, it owns the body scroll lock.
    // Re-apply its lock instead of releasing entirely — avoids layout jump on the info panel.
    // Use the same top calculation as applyKiwiBodyScrollGuard so both locks are consistent.
    var accordionEl = document.querySelector('accordian-drawer');
    if (accordionEl && typeof accordionEl._scrollLockY === 'number') {
      var lockY = accordionEl._scrollLockY;
      var lockHeaderHeight = getHeaderHeightKiwi();
      var lockTop = isMobileViewport()
        ? '-' + Math.round(Math.max(lockY - lockHeaderHeight, 0)) + 'px'
        : '-' + Math.round(lockY) + 'px';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.setProperty('position', 'fixed', 'important');
      document.body.style.setProperty('top', lockTop, 'important');
      document.body.style.setProperty('left', '0', 'important');
      document.body.style.setProperty('right', '0', 'important');
      document.body.style.setProperty('width', '100%', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
      return;
    }

    if (document.body) {
      ['position', 'top', 'left', 'right', 'width', 'overflow'].forEach(function (prop) {
        document.body.style.setProperty(prop, '', 'important');
        document.body.style.removeProperty(prop);
      });
      document.body.classList.remove('overflow-hidden');
    }
    if (document.documentElement) {
      document.documentElement.style.overflow = '';
      document.documentElement.style.removeProperty('overflow');
      document.documentElement.classList.remove('pdp-sidebar-scroll-lock');
    }
    document.body.classList.remove('pdp-sidebar-scroll-lock');
  }

  // Exposed globally — the size-popup/color-popup IIFE further down this file
  // is a separate closure and can't see this function otherwise.
  window.releaseAllPageScrollLocks = releaseAllPageScrollLocks;

  function readSavedScrollYFromBody() {
    if (kiwiScrollGuardState.hasSavedScroll) return kiwiScrollGuardState.scrollY;
    if (!document.body) return window.scrollY || 0;
    var bodyTop = parseInt(document.body.style.top || '0', 10);
    if (!isNaN(bodyTop) && bodyTop < 0) return -bodyTop;
    return window.scrollY || 0;
  }

  window.__pdpKiwiSaveScrollPosition = function (y) {
    kiwiScrollGuardState.scrollY = Math.round(typeof y === 'number' ? y : window.scrollY);
    kiwiScrollGuardState.hasSavedScroll = true;
  };

  /** Keep body visually at scrollY when position:fixed (Kiwi otherwise jumps to top). */
  function assertKiwiBodyScrollTop(scrollY) {
    if (!document.body || document.body.style.position !== 'fixed') return;
    var headerHeight = getKiwiScrollLockHeaderOffset();
    var expectedTop = isMobileViewport() ? '-' + Math.round(Math.max(scrollY - headerHeight, 0)) + 'px' : '-' + Math.round(scrollY) + 'px';
    if (document.body.style.top !== expectedTop) {
      document.body.style.setProperty('top', expectedTop, 'important');
    }
  }

  /**
   * Call synchronously on size-chart open (before Kiwi runs).
   * Locks scroll position, pins header, and pauses parallax so the page does not jump.
   *
   * Order matters:
   * 1. pinHeader() — reads sh.offsetHeight while body is in normal flow (no fixed yet).
   *    Sets section-header { position:fixed } + locks sticky-header height in ONE recalc.
   * 2. Snapshot --header-parent-height — capture the post-pin computed value as !important
   *    so Kiwi's subsequent body writes can't shift the sticky media-wrapper.
   * 3. applyKiwiBodyScrollGuard — sets body { position:fixed; top:-scrollY } AFTER header
   *    is already pinned, so the layout read inside pinHeader sees clean geometry.
   * 4. _parallaxFreezeHeader — extra freeze for parallax animation system.
   */
  function prepareKiwiOpen(scrollY) {
    if (typeof scrollY !== 'number' || isNaN(scrollY)) {
      scrollY = readSavedScrollYFromBody();
    }
    window.__pdpKiwiSaveScrollPosition(scrollY);
    window.__pdpKiwiSettling = false;
    window.__pdpKiwiModalOpen = true;

    // Freeze the parallax reveal before any scroll-lock DOM writes below — see the
    // pause/resume definitions near the top of this file for why.
    if (window._parallaxPausePanelReveal) window._parallaxPausePanelReveal();

    var sidebarOpen =
      typeof window.isPdpSidebarOpen === 'function' && window.isPdpSidebarOpen();
    var isParallaxCollapsed = isParallaxPanelCollapsed();

    // Step 1: pin header BEFORE body goes fixed — layout read happens in clean state
    // Only pin header if parallax is NOT collapsed!
    if (!isParallaxCollapsed && headerPinState.count === 0) {
      pinHeader();
    }

    // Step 2: snapshot --header-parent-height at post-pin value
    var hph = getComputedStyle(document.documentElement).getPropertyValue('--header-parent-height');
    if (hph && hph.trim()) {
      document.documentElement.style.setProperty('--header-parent-height', hph.trim(), 'important');
    }

    // Step 3: lock body scroll position
    if (!sidebarOpen) {
      applyKiwiBodyScrollGuard(scrollY);
    } else {
      assertKiwiBodyScrollTop(scrollY);
    }

    // Step 4: freeze header for parallax system
    if (window._parallaxFreezeHeader) window._parallaxFreezeHeader();
  }
  window.__pdpKiwiPrepareOpen = prepareKiwiOpen;

  function reattachStickyHeaderScroll() {
    var sh = document.querySelector('sticky-header');
    if (!sh || !sh.onScrollHandler) return;
    window.removeEventListener('scroll', sh.onScrollHandler);
    sh.currentScrollTop = window.scrollY;
    window.addEventListener('scroll', sh.onScrollHandler, false);
  }

  var headerPinState = window.__pdpHeaderPinState || (window.__pdpHeaderPinState = {
    count: 0,
    styles: null,
    shEl: null,
    shHeight: 0,
  });

  function saveHeaderStyles(el) {
    return {
      position: el.style.getPropertyValue('position'),
      top: el.style.getPropertyValue('top'),
      left: el.style.getPropertyValue('left'),
      right: el.style.getPropertyValue('right'),
      zIndex: el.style.getPropertyValue('z-index')
    };

  }

  function restoreHeaderStyles(el, styles) {
    if (!el || !styles) return;
    if (styles.position) el.style.setProperty('position', styles.position);
    else el.style.removeProperty('position');
    if (styles.top) el.style.setProperty('top', styles.top);
    else el.style.removeProperty('top');
    if (styles.left) el.style.setProperty('left', styles.left);
    else el.style.removeProperty('left');
    if (styles.right) el.style.setProperty('right', styles.right);
    else el.style.removeProperty('right');
    if (styles.zIndex) el.style.setProperty('z-index', styles.zIndex);
    else el.style.removeProperty('z-index');
  }

  function pinHeader() {
    if (!isMobileViewport()) return;
    if (headerPinState.count > 0) {
      headerPinState.count += 1;
      return;
    }

    var el = document.querySelector('.section-header');
    var sh = document.querySelector('sticky-header');
    if (!el || !sh) return;

    // Pinning .section-header (the outer wrapper) itself to position:fixed pulls it
    // out of the document/grid flow entirely — its track collapses and everything
    // below (the whole product panel, including Add to Cart) snaps up by one
    // header-height the instant the style lands, regardless of any height locked on
    // the inner sticky-header. Fix: lock .section-header's OWN height (keeps its flow
    // track reserved) and only make the INNER <sticky-header> element position:fixed,
    // matching the proven pattern in pdp-info-sidebar.js's pinHeaderNoShift.
    headerPinState.styles = saveHeaderStyles(sh);
    headerPinState.shEl = el;
    headerPinState.shHeight = el.offsetHeight;
    if (headerPinState.shHeight > 0) {
      el.style.setProperty('height', headerPinState.shHeight + 'px', 'important');
      el.style.setProperty('min-height', headerPinState.shHeight + 'px', 'important');
    }

    // Header may still sit below the announcement bar (not yet scrolled past it) —
    // pinning to top:0 would yank it out of place and cover the bar instead of
    // sitting where it visually already was.
    var top = Math.max(0, sh.getBoundingClientRect().top);
    // Suppress transition before pinning so position change is instant.
    sh.style.setProperty('transition', 'none', 'important');
    sh.style.setProperty('position', 'fixed', 'important');
    sh.style.setProperty('top', top + 'px', 'important');
    sh.style.setProperty('left', '0', 'important');
    sh.style.setProperty('right', '0', 'important');
    sh.style.setProperty('z-index', '99999', 'important');
    sh.preventHide = true;
    if (sh.onScrollHandler) window.removeEventListener('scroll', sh.onScrollHandler);
    headerPinState.count = 1;
  }

  function unpinHeader() {
    if (headerPinState.count === 0) return;
    headerPinState.count -= 1;
    if (headerPinState.count > 0) return;

    var sh = document.querySelector('sticky-header');
    if (!sh) return;

    // Kill any CSS transition on the header before restoring position so
    // it can't animate from its fixed position back — that movement is
    // the flicker the user sees when the size chart closes.
    sh.style.setProperty('transition', 'none', 'important');

    restoreHeaderStyles(sh, headerPinState.styles);
    headerPinState.styles = null;

    if (headerPinState.shEl) {
      headerPinState.shEl.style.removeProperty('height');
      headerPinState.shEl.style.removeProperty('min-height');
      headerPinState.shEl = null;
    }
    headerPinState.shHeight = 0;

    // Re-enable transitions after one frame so normal scroll animations still work.
    requestAnimationFrame(function () {
      sh.style.removeProperty('transition');
    });

    sh.currentScrollTop = window.scrollY;
    sh.preventHide = false;
    if (sh.onScrollHandler) {
      window.removeEventListener('scroll', sh.onScrollHandler);
      setTimeout(function () {
        var sh2 = document.querySelector('sticky-header');
        if (!sh2 || !sh2.onScrollHandler) return;
        sh2.currentScrollTop = window.scrollY;
        window.addEventListener('scroll', sh2.onScrollHandler, false);
      }, 200);
    }
  }

  window.pdpPinHeader = pinHeader;
  window.pdpUnpinHeader = unpinHeader;

  /** Restore scroll + header in one atomic sequence so the info panel does not jump. */
  function settleAfterKiwiClose(savedScrollY, bodyObs) {
    if (window.__pdpKiwiSettling) return;
    window.__pdpKiwiSettling = true;
    if (bodyObs) bodyObs.disconnect();

    if (typeof savedScrollY !== 'number' || savedScrollY < 0) {
      savedScrollY = readSavedScrollYFromBody();
    }
    kiwiScrollGuardState.scrollY = savedScrollY;

    // Check if size peek popup is still open
    var sizePopupStillOpen = document.querySelector('.size-popup-wrapper.is-open');

    var sidebarStillOpen =
      typeof window.isPdpSidebarOpen === 'function' && window.isPdpSidebarOpen();

    // Accordion drawer is open — it owns the body scroll lock and the page position.
    // Skip header unpin / parallax thaw / scroll restoration so the product info doesn't flicker.
    var accordionEl = document.querySelector('accordian-drawer');
    var accordionStillOpen = accordionEl && typeof accordionEl._scrollLockY === 'number';

    function endSettle() {
      window.__pdpKiwiModalOpen = false;
      kiwiScrollGuardState.hasSavedScroll = false;
      kiwiScrollGuardState.active = false;
      kiwiScrollGuardState.styles = null;
      if (!sidebarStillOpen && !sizePopupStillOpen) {
        releaseAllPageScrollLocks();
        reattachStickyHeaderScroll();
      }
      window.__pdpKiwiSettling = false;
      // Sync --pdp-vh after modal close — toolbar may have changed state
      // while the modal was open, leaving the frozen gallery height stale.
      if (typeof window._pdpVhSync === 'function') window._pdpVhSync();
    }

    if (headerPinState.count > 0 && !sizePopupStillOpen && !accordionStillOpen) {
      headerPinState.count = 1;
      unpinHeader();
    }

    if (sidebarStillOpen || sizePopupStillOpen || accordionStillOpen) {
      endSettle();
      return;
    }

    if (window._parallaxThawHeader) window._parallaxThawHeader();

    // Disable scroll anchoring during the restore sequence — closing the modal
    // can cause the toolbar to show/hide, triggering a scroll-anchor adjustment
    // that fights our scrollTo(savedScrollY) calls.
    document.documentElement.style.setProperty('overflow-anchor', 'none');
    document.body.style.setProperty('overflow-anchor', 'none');

    // scrollTo BEFORE releaseAllPageScrollLocks — removing body position:fixed snaps
    // scrollY to 0 for one paint frame; setting scroll first while body is still fixed
    // means the browser restores the correct position atomically when fixed is removed.
    window.scrollTo(0, savedScrollY);

    releaseAllPageScrollLocks();
    // Re-assert synchronously, same tick — releaseAllPageScrollLocks() just made the
    // document scrollable again with its real scrollY (0, frozen there the whole time
    // the lock was on). Without this, that 0 position is what the browser paints on
    // the very next frame (the whole panel including Add to Cart flashes up to the
    // top), and the first correction below only lands one requestAnimationFrame later
    // — one frame too late to prevent that paint.
    window.scrollTo(0, savedScrollY);

    var sh = document.querySelector('sticky-header');
    if (sh) {
      sh.preventHide = false;
      sh.preventReveal = false;
      sh.currentScrollTop = savedScrollY;
    }

    requestAnimationFrame(function () {
      window.scrollTo(0, savedScrollY);
      requestAnimationFrame(function () {
        window.scrollTo(0, savedScrollY);
        if (sh) sh.currentScrollTop = savedScrollY;
        // Release overflow-anchor and snapshot vars together after scroll is stable.
        document.documentElement.style.removeProperty('overflow-anchor');
        document.body.style.removeProperty('overflow-anchor');
        document.documentElement.style.removeProperty('--header-parent-height');
        // Scroll is genuinely restored and unlocked at this point — safe to resume the
        // reveal trigger so it recomputes progress against the real position.
        if (window._parallaxResumePanelReveal) window._parallaxResumePanelReveal();
        endSettle();
      });
    });
  }
  window.__pdpSettleAfterKiwiClose = settleAfterKiwiClose;

  // Capture scrollY on touchstart — before iOS toolbar appears on tap.
  // The click event fires AFTER the toolbar shows (innerHeight shrinks, scrollY bumps),
  // so window.scrollY in the click handler is already toolbar-adjusted and wrong.
  // We snapshot it here where it's still clean.
  var _kiwiTouchStartScrollY = -1;
  var _kiwiTouchStartTime = 0;
  document.addEventListener('touchstart', function (e) {
    if (!e.target.closest(KIWI_LINK_SEL)) return;
    // Don't use scrollY=0 if body is fixed (accordion/sidebar scroll lock)
    if (document.body && document.body.style.position === 'fixed') return;
    _kiwiTouchStartScrollY = window.scrollY;
    _kiwiTouchStartTime = Date.now();
  }, { passive: true });

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest(KIWI_LINK_SEL);
    if (!trigger) return;
    if (!isMobileViewport()) return;
    // pdp-info-sidebar.js redirects this same Kiwi link click into its own sidebar
    // (instead of Kiwi's popup) and already handles scroll lock/header pin itself —
    // skip this popup-oriented lock so the two don't fight over body position:fixed.
    if (window.__pdpSidebarClaimsKiwi) return;
    // __pdpSidebarClaimsKiwi above only covers fireKiwiSuppressed's synthetic
    // re-dispatch — it's still false on the ORIGINAL trusted click, and this
    // listener (registered first: product-parallax-mobile.js loads before
    // pdp-info-sidebar.js) fires before pdp-info-sidebar's own capture-phase
    // handler gets a turn. stopPropagation() there can't stop a sibling listener
    // on the same document node, so both systems ran prepareKiwiOpen()/open() for
    // the same click — double header-pin, double scroll-lock — corrupting the
    // mobile info panel's state on close. Bail out here for any trigger routed to
    // the sidebar (data-size-guide-trigger, set in product-swatches.liquid) so
    // pdp-info-sidebar.js owns the whole flow exclusively.
    if (trigger.closest('[data-size-guide-trigger="true"]')) return;

    var triggerHref = trigger.getAttribute && trigger.getAttribute('href');
    if (trigger.tagName === 'A' && (!triggerHref || triggerHref === '#' || triggerHref.charAt(0) === '#')) {
      e.preventDefault();
    }

    // Use the touchstart-captured scrollY if available and recent (within 1s).
    // This avoids the toolbar-appear scroll-anchor bump that makes window.scrollY
    // wrong by the time the click event fires.
    // Also try visualViewport.pageTop — on iOS this is NOT affected by scroll-anchoring
    // and gives the true scroll position even after the toolbar animation.
    var vpScrollY = (window.visualViewport && typeof window.visualViewport.pageTop === 'number')
      ? Math.round(window.visualViewport.pageTop)
      : -1;

    var savedScrollY;
    if (_kiwiTouchStartScrollY >= 0 && (Date.now() - _kiwiTouchStartTime) < 1000) {
      // Touchstart captured — most reliable (pre-toolbar)
      savedScrollY = _kiwiTouchStartScrollY;
    } else if (vpScrollY >= 0) {
      // visualViewport.pageTop — unaffected by scroll-anchor
      savedScrollY = vpScrollY;
    } else {
      savedScrollY = window.scrollY;
    }
    _kiwiTouchStartScrollY = -1; // consume
    _kiwiTouchStartTime = 0;
    // If body is already position:fixed (e.g. accordion drawer scroll lock is active),
    // window.scrollY returns 0. Recover the real scroll position from body.style.top.
    // On mobile the accordion sets top = -(scrollY - headerHeight), so add headerHeight back.
    if (document.body && document.body.style.position === 'fixed') {
      var bodyTopVal = parseInt(document.body.style.top || '0', 10);
      if (!isNaN(bodyTopVal) && bodyTopVal < 0) {
        var recoveredY = -bodyTopVal;
        // Check if this came from an accordion lock (which subtracts headerHeight on mobile)
        var accordionEl2 = document.querySelector('accordian-drawer');
        if (accordionEl2 && typeof accordionEl2._scrollLockY === 'number') {
          // Accordion stores the original scrollY — use it directly, no need to re-derive
          savedScrollY = accordionEl2._scrollLockY;
        } else {
          savedScrollY = recoveredY;
        }
      }
    }
    // If size peek popup is open, use the saved scrollY from when size peek was opened!
    var sizePopupWrapper = document.querySelector('.size-popup-wrapper.is-open');
    if (sizePopupWrapper && typeof window.sizePopupOpenScrollY === 'number' && window.sizePopupOpenScrollY > 0) {
      savedScrollY = window.sizePopupOpenScrollY;
    }
    prepareKiwiOpen(savedScrollY);
    // If size peek is open, explicitly re-apply the body scroll guard to make sure it's set!
    if (sizePopupWrapper) {
      applyKiwiBodyScrollGuard(savedScrollY);
    }

    var done = false;
    function cleanup() {
      if (done) return;
      done = true;
      settleAfterKiwiClose(savedScrollY, bodyObs);
    }

    var kiwiHasLocked = false;
    // Snapshot scrollY here — Kiwi will overwrite body.top to 0, making
    // readSavedScrollYFromBody() return 0 by the time the MutationObserver fires.
    var preKiwiScrollY = savedScrollY;
    function isKiwiModalVisible() {
      return (
        document.body.classList.contains('kiwi-sizing-modal-visible') ||
        document.body.classList.contains('vanilla-modal-visible') ||
        document.documentElement.classList.contains('kiwi-sizing-modal-visible') ||
        document.documentElement.classList.contains('vanilla-modal-visible') ||
        !!document.querySelector('.kiwi-sizing-modal-visible, .vanilla-modal-visible')
      );
    }
    var bodyObs = new MutationObserver(function () {
      // If the persistent openGuard is active, let it handle corrections.
      // Only intervene here during the pre-modal-visible window.
      if (kiwiHasLocked) {
        // Modal is open — openGuard handles top correction persistently.
        // Watch only for modal close.
        if (!isKiwiModalVisible()) {
          settleAfterKiwiClose(preKiwiScrollY, bodyObs);
          done = true;
        }
        return;
      }

      // Pre-modal: correct body.top using the same formula as applyKiwiBodyScrollGuard.
      var currentHeaderHeight = getKiwiScrollLockHeaderOffset();
      var correctTop = isMobileViewport()
        ? '-' + Math.round(Math.max(preKiwiScrollY - currentHeaderHeight, 0)) + 'px'
        : '-' + Math.round(preKiwiScrollY) + 'px';
      if (document.body.style.top !== correctTop || document.body.style.position !== 'fixed') {
        document.body.style.setProperty('top', correctTop, 'important');
        document.body.style.setProperty('position', 'fixed', 'important');
      }

      if (isKiwiModalVisible()) {
        // Use preKiwiScrollY — Kiwi may have already overwritten body.top to 0
        // so readSavedScrollYFromBody() would return 0 (corrupt value).
        kiwiScrollGuardState.scrollY = preKiwiScrollY;
        kiwiScrollGuardState.hasSavedScroll = true;
        // Re-apply correct body.top one final time before handing off to openGuard.
        document.body.style.setProperty('top', correctTop, 'important');
        document.body.style.setProperty('position', 'fixed', 'important');
        kiwiHasLocked = true;
      }
    });
    bodyObs.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
    bodyObs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    setTimeout(cleanup, 15000);
  }, true);
})();

/**
 * Size-peek: open the size selector panel above the sticky ATC bar on mobile.
 * Now uses position:fixed on body (same as color popup) for consistency.
 */
(function () {
  function isMobile() {
    return window.innerWidth <= 1023;
  }

  function getInfoWrapper() {
    return document.querySelector('.product-paralax .product__info-wrapper');
  }

  function getSizeFieldset() {
    return document.querySelector('.product-paralax .product-form__input--pill');
  }

  var sizePopupWrapper = null;
  var sizeClipContainer = null;
  var sizeBackdrop = null;
  var originalSizeParent = null;
  var originalSizeNextSibling = null;
  var sizePopupOpenScrollY = 0;
  var scrollAnchoringState = null;
  var sizePopupVariantUnsubscriber = null;
  var sizePopupKiwiPoll = null;
  var sizePopupKiwiReloadTimer = null;
  var sizePopupCloseTimer = null;

  function getPopupSizeFieldset() {
    return sizePopupWrapper ? sizePopupWrapper.querySelector('.product-form__input--pill') : null;
  }

  function getLiveVariantSelects() {
    var productRoot = document.querySelector('.product-paralax');
    return (productRoot && productRoot.querySelector('variant-selects')) || document.querySelector('variant-selects');
  }

  function clearSizePopupKiwiWaiters() {
    if (sizePopupKiwiPoll) {
      clearInterval(sizePopupKiwiPoll);
      sizePopupKiwiPoll = null;
    }
    if (sizePopupKiwiReloadTimer) {
      clearTimeout(sizePopupKiwiReloadTimer);
      sizePopupKiwiReloadTimer = null;
    }
  }

  function bindSizePopupClose(sizeFieldset) {
    if (!sizeFieldset) return;
    var closeBtn = sizeFieldset.querySelector('.size-peek-close');
    if (!closeBtn || closeBtn.dataset.sizePeekBound === 'true') return;
    closeBtn.dataset.sizePeekBound = 'true';
    closeBtn.addEventListener('click', function () {
      window.sizePeekClose();
    });
  }

  function initSizePopupKiwiChart(sizeFieldset) {
    var chart = sizeFieldset && sizeFieldset.querySelector('#KiwiSizingChart, [data-kiwi-size-chart]');
    if (!chart) return;

    clearSizePopupKiwiWaiters();

    sizePopupKiwiReloadTimer = setTimeout(function () {
      sizePopupKiwiReloadTimer = null;
      if (!sizePopupWrapper || !sizePopupWrapper.classList.contains('is-open') || !chart.isConnected) return;

      var existingTrigger = chart.querySelector('.ks-chart-modal-link, .ks-chart-modal-button, .ks-calculator-modal-link, .ks-calculator-modal-button') || chart.querySelector('a, button');
      if (existingTrigger) {
        document.dispatchEvent(new CustomEvent('kiwi:reloaded', { detail: { chart: chart } }));
        return;
      }

      var load = function () {
        if (!sizePopupWrapper || !sizePopupWrapper.classList.contains('is-open') || !chart.isConnected) return;
        chart.innerHTML = '';

        var displayMode = Number(chart.dataset.displayMode);
        var options = { el: chart };
        if (!isNaN(displayMode)) options.displayMode = displayMode;
        if (chart.dataset.linkText) options.buttonText = chart.dataset.linkText;

        window.ks.loadSizing({
          productData: {
            product: chart.dataset.product,
            tags: chart.dataset.tags || '',
            type: chart.dataset.type || '',
            vendor: chart.dataset.vendor || '',
            collections: chart.dataset.collections || ''
          },
          options: options,
          loaded: function () {
            if (!chart.isConnected) return;
            document.dispatchEvent(new CustomEvent('kiwi:reloaded', { detail: { chart: chart } }));
          }
        });
      };

      if (window.ks && typeof window.ks.loadSizing === 'function') {
        load();
        return;
      }

      sizePopupKiwiPoll = setInterval(function () {
        if (!window.ks || typeof window.ks.loadSizing !== 'function') return;
        clearInterval(sizePopupKiwiPoll);
        sizePopupKiwiPoll = null;
        load();
      }, 200);

      setTimeout(function () {
        if (!sizePopupKiwiPoll) return;
        clearInterval(sizePopupKiwiPoll);
        sizePopupKiwiPoll = null;
      }, 5000);
    }, 120);
  }

  function syncSizePopupFieldset() {
    if (!sizePopupWrapper || !sizePopupWrapper.classList.contains('is-open')) return;

    var newVariantSelects = getLiveVariantSelects();
    if (!newVariantSelects) return;

    var newFieldset = newVariantSelects.querySelector('.product-form__input--pill');
    if (!newFieldset) return;
    if (newFieldset.parentNode === sizePopupWrapper) {
      bindSizePopupClose(newFieldset);
      var existingChart = sizePopupWrapper.querySelector('#KiwiSizingChart');
      if (existingChart) existingChart.style.display = 'none';
      return;
    }

    var oldFieldset = getPopupSizeFieldset();
    if (oldFieldset) oldFieldset.remove();

    originalSizeParent = newFieldset.parentNode;
    originalSizeNextSibling = newFieldset.nextSibling;
    sizePopupWrapper.appendChild(newFieldset);
    bindSizePopupClose(newFieldset);
    var newChart = sizePopupWrapper.querySelector('#KiwiSizingChart');
    if (newChart) newChart.style.display = 'none';
    initSizePopupKiwiChart(newFieldset);
  }

  function updateSizePopupClip(infoWrapper) {
    if (!sizeClipContainer || !infoWrapper) return;

    var infoRect = infoWrapper.getBoundingClientRect();

    // Pin clip to the top of the viewport so no blank gap shows above the popup.
    var top = 0;

    // Height should reach from the top of the viewport down to the info wrapper's top edge.
    // Using infoRect.top (viewport coordinates) ensures the clip covers the visible area above the info panel.
    var height = Math.max(0, Math.round(infoRect.top));

    // Guard: never exceed viewport height
    if (height > window.innerHeight) height = window.innerHeight;

    sizeClipContainer.style.top = top + 'px';
    sizeClipContainer.style.height = (height + 1) + 'px'; // +1 to avoid 1px gap at top of clip
  }

  function disableScrollAnchoring() {
    if (scrollAnchoringState) return;
    scrollAnchoringState = {
      html: document.documentElement ? document.documentElement.style.getPropertyValue('overflow-anchor') : '',
      body: document.body ? document.body.style.getPropertyValue('overflow-anchor') : ''
    };
    if (document.documentElement) {
      document.documentElement.style.setProperty('overflow-anchor', 'none');
    }
    if (document.body) {
      document.body.style.setProperty('overflow-anchor', 'none');
    }
  }

  function restoreScrollAnchoring() {
    if (!scrollAnchoringState) return;
    if (document.documentElement) {
      if (scrollAnchoringState.html) document.documentElement.style.setProperty('overflow-anchor', scrollAnchoringState.html);
      else document.documentElement.style.removeProperty('overflow-anchor');
    }
    if (document.body) {
      if (scrollAnchoringState.body) document.body.style.setProperty('overflow-anchor', scrollAnchoringState.body);
      else document.body.style.removeProperty('overflow-anchor');
    }
    scrollAnchoringState = null;
  }

  function buildSizePopup() {
    if (sizePopupWrapper) return;

    // Clip container: appended to body, position:fixed — viewport-level positioning.
    // z-index 202, below .product-paralax stacking context (z-index:203) so info + gallery paint above.
    sizeClipContainer = document.createElement('div');
    sizeClipContainer.className = 'size-popup-clip';
    document.body.appendChild(sizeClipContainer);

    // Backdrop lives inside clip container — receives clicks in gallery area above popup
    sizeBackdrop = document.createElement('div');
    sizeBackdrop.className = 'size-peek-backdrop';
    sizeBackdrop.setAttribute('aria-hidden', 'true');
    sizeBackdrop.style.display = 'none';
    sizeClipContainer.appendChild(sizeBackdrop);

    // Click backdrop to close
    sizeBackdrop.addEventListener('click', function () {
      window.sizePeekClose();
    });

    // Create wrapper for size fieldset (lives inside clip container, above backdrop)
    sizePopupWrapper = document.createElement('div');
    sizePopupWrapper.className = 'size-popup-wrapper';
    sizeClipContainer.appendChild(sizePopupWrapper);

    ['click', 'touchstart', 'touchmove', 'touchend', 'pointerdown'].forEach(function (eventName) {
      sizePopupWrapper.addEventListener(eventName, function (e) {
        // Let Kiwi "Size & Fit Guide" clicks bubble to document — the document-level
        // click listener elsewhere in this file (KIWI_LINK_SEL) is what actually opens
        // the Kiwi modal and applies the header/scroll fixes; stopping it here silently
        // breaks the link when tapped from inside this popup.
        if (e.target.closest('.ks-chart-modal-link, .ks-chart-modal-button, .ks-calculator-modal-link, .ks-calculator-modal-button')) return;
        e.stopPropagation();
      }, { passive: eventName !== 'click' });
    });

    // The fieldset lives outside <variant-selects> while the popup is open, so
    // change events never reach it. Fix: synchronously move it back, fire the
    // event (bubbles through variant-selects), then move it back into the popup
    // — all in one JS tick so there is no visible flicker.
    //
    // After the AJAX re-render, viewTransition inserts a new <variant-selects>
    // with a new fieldset that shares the same radio-group (name + form). Keep
    // the popup synced with that live fieldset so both radios and Kiwi chart
    // update correctly after every variant refresh while the popup is open.
    sizePopupWrapper.addEventListener('change', function (e) {
      if (e.target.type !== 'radio' || !sizePopupWrapper.classList.contains('is-open')) return;
      var changedInput = e.target;
      moveSizeFieldsetBack();
      changedInput.dispatchEvent(new Event('change', { bubbles: true }));
      moveSizeFieldsetToPopup();
    });

    if (!sizePopupVariantUnsubscriber) {
      sizePopupVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, function () {
        syncSizePopupFieldset();
      });
    }
  }

  function moveSizeFieldsetToPopup() {
    var sizeFieldset = getSizeFieldset();
    if (!sizeFieldset || !sizePopupWrapper) return null;

    // Remember where it came from
    originalSizeParent = sizeFieldset.parentNode;
    originalSizeNextSibling = sizeFieldset.nextSibling;

    // Move it into the popup
    sizePopupWrapper.appendChild(sizeFieldset);

    return sizeFieldset;
  }

  function moveSizeFieldsetBack() {
    var sizeFieldset = getPopupSizeFieldset();
    if (!sizeFieldset || !originalSizeParent) return;

    // Move it back
    if (originalSizeNextSibling) {
      originalSizeParent.insertBefore(sizeFieldset, originalSizeNextSibling);
    } else {
      originalSizeParent.appendChild(sizeFieldset);
    }
  }

  /**
   * Open the size-peek popup.
   */
  // Pre-capture scrollY on touchstart for any trigger that opens the size popup,
  // for the same reason as the Kiwi touchstart fix above.
  var _sizePeekTouchStartScrollY = -1;
  (function () {
    var SIZE_PEEK_TRIGGER_SEL = '.product-form__submit, .size-tab, .size-peek-trigger';
    document.addEventListener('touchstart', function (e) {
      if (!e.target.closest(SIZE_PEEK_TRIGGER_SEL)) return;
      if (document.body && document.body.style.position === 'fixed') return;
      _sizePeekTouchStartScrollY = window.scrollY;
    }, { passive: true });
  })();

  window.sizePeekOpen = function () {
    if (!isMobile()) return;
    var infoWrapper = getInfoWrapper();
    if (!infoWrapper) return;

    // If color popup is open, close it first
    var colorPopup = document.querySelector('.color-image-popup');
    if (colorPopup && colorPopup.classList.contains('is-open')) {
      // Close color popup
      var colorBackdrop = document.querySelector('.color-peek-backdrop');
      if (colorBackdrop) colorBackdrop.style.display = 'none';
      colorPopup.classList.remove('is-open');

      // Wait for color popup to close before opening size popup
      setTimeout(function () {
        openSizePopup(infoWrapper);
      }, 400);
      return;
    }

    openSizePopup(infoWrapper);
  };

  function openSizePopup(infoWrapper) {
    buildSizePopup();

    // Add class to prevent snap scrolling FIRST — before any DOM changes
    infoWrapper.classList.add('size-peek-open');

    // Freeze header BEFORE any DOM moves — DOM reflow from moveSizeFieldsetToPopup
    // fires scroll events; preventHide must be set before that happens.
    // This also keeps the mobile header visible while the size sheet is open.

    // Anchor scroll position across DOM move — browser may re-anchor scrollY on reflow.
    // Use touchstart-captured scrollY if available — toolbar appear on tap bumps
    // window.scrollY by ~50px before we get here, giving the wrong lock position.
    var savedScrollY = (_sizePeekTouchStartScrollY >= 0) ? _sizePeekTouchStartScrollY : window.scrollY;
    _sizePeekTouchStartScrollY = -1; // consume
    sizePopupOpenScrollY = savedScrollY;
    window.sizePopupOpenScrollY = savedScrollY;
    disableScrollAnchoring();

    // Keep header visible while size sheet is open — _parallaxFreezeHeader (not pdpPinHeader)
    // so we don't change position:fixed on the header, which would hide the announcement bar.
    if (window._parallaxFreezeHeader) window._parallaxFreezeHeader();

    // Flush any pending close so the fieldset is back before we move it again
    if (sizePopupCloseTimer) {
      window.clearTimeout(sizePopupCloseTimer);
      sizePopupCloseTimer = null;
      closeSizePopupNow();
    }

    // Move the original fieldset into the popup
    var sizeFieldset = moveSizeFieldsetToPopup();
    if (!sizeFieldset) {
      restoreScrollAnchoring();
      if (window._parallaxThawHeader) window._parallaxThawHeader();
      return;
    }
    bindSizePopupClose(sizeFieldset);

    // Restore scrollY if DOM move caused browser to re-anchor
    if (window.scrollY !== savedScrollY) {
      window.scrollTo(0, savedScrollY);
    }

    // Clip container stays aligned to the scroll position that was visible when
    // the popup opened, while still clipping at the current info wrapper edge.
    updateSizePopupClip(infoWrapper);
    sizePopupWrapper.style.bottom = '0px';
    if (sizeBackdrop) {
      // backdrop fills clip container via CSS inset:0 — no inline positioning needed
    }

    // Open with animation
    sizePopupWrapper.getBoundingClientRect();
    sizePopupWrapper.classList.add('is-open');
    if (sizeBackdrop) {
      sizeBackdrop.style.display = 'block';
      requestAnimationFrame(function () {
        sizeBackdrop.classList.add('is-visible');
      });
    }

  }

  function closeSizePopupNow() {
    var infoWrapper = getInfoWrapper();
    if (infoWrapper) {
      infoWrapper.classList.remove('size-peek-open');
    }
    clearSizePopupKiwiWaiters();
    moveSizeFieldsetBack();
    sizePopupOpenScrollY = 0;
    window.sizePopupOpenScrollY = 0;
    restoreScrollAnchoring();
    var popupWarning = document.querySelector('.size-popup-warning');
    if (popupWarning) popupWarning.hidden = true;
    if (window._parallaxThawHeader) window._parallaxThawHeader();
    // Now that size peek is closed, release body locks if needed!
    // (defined in a separate IIFE above — call via window)
    if (typeof window.releaseAllPageScrollLocks === 'function') window.releaseAllPageScrollLocks();
  }

  window.sizePeekClose = function (opts) {
    var instant = opts && opts.instant;
    var clipClose = opts && opts.clipClose;
    if (sizePopupCloseTimer) {
      window.clearTimeout(sizePopupCloseTimer);
      sizePopupCloseTimer = null;
    }
    if (sizeBackdrop) {
      sizeBackdrop.classList.remove('is-visible');
      if (instant) {
        sizeBackdrop.style.display = 'none';
      } else {
        setTimeout(function () { sizeBackdrop.style.display = 'none'; }, 350);
      }
    }
    if (sizePopupWrapper) {
      if (clipClose) {
        // Disable CSS transform transition — clip container height will mask the popup
        // as the info panel scrolls over it. No competing slide-down animation.
        sizePopupWrapper.style.transition = 'none';
        requestAnimationFrame(function () {
          if (sizePopupWrapper) sizePopupWrapper.style.transition = '';
        });
      }
      sizePopupWrapper.classList.remove('is-open');
    }
    if (instant) {
      closeSizePopupNow();
    } else {
      sizePopupCloseTimer = setTimeout(function () {
        sizePopupCloseTimer = null;
        closeSizePopupNow();
      }, 400);
    }
  };

  // Keep size-peek state accurate on resize
  window.addEventListener('resize', function () {
    if (sizePopupWrapper && sizePopupWrapper.classList.contains('is-open')) {
      var infoWrapper = getInfoWrapper();
      if (infoWrapper) {
        var infoRect = infoWrapper.getBoundingClientRect();
        var infoBarHeight = window.innerHeight - infoRect.top;
        updateSizePopupClip(infoWrapper);
        if (sizeBackdrop) sizeBackdrop.style.bottom = infoBarHeight + 'px';
      }
    }
  });

  function handleOutsideSizePopup(e) {
    if (!sizePopupWrapper || !sizePopupWrapper.classList.contains('is-open')) return;
    if (sizePopupWrapper.contains(e.target)) return;
    if (sizeBackdrop && sizeBackdrop.contains(e.target)) return;
    // Don't close if click originated from the trigger (Select Size button / size strip in collapsed bar)
    if (e.target.closest('.product-form__submit')) return;
    if (e.target.closest('.size-tab')) return;
    if (e.target.closest('.pdp-bis__trigger')) return;
    window.sizePeekClose();
  }
  document.addEventListener('pointerdown', handleOutsideSizePopup, true);
  document.addEventListener('click', handleOutsideSizePopup, true);

})();

/**
 * Color image popup — tapping any color swatch in the collapsed parallax bar on mobile
 * opens a panel above the info wrapper showing ALL swatch product images as a scrollable
 * carousel, with the tapped swatch's image scrolled into view and highlighted.
 */
(function () {
  function isMobile() {
    return window.innerWidth <= 1023;
  }

  function getInfoWrapper() {
    return document.querySelector('.product-paralax .product__info-wrapper');
  }

  var popup = null;
  var colorClipContainer = null;
  var trackEl = null;
  var colorEl = null;
  var backdrop = null;
  var suppressScrollClose = false;
  var isClosingForATC = false;
  var lastHandledSwatch = null;
  var lastHandledSwatchAt = 0;

  function buildPopup() {
    if (popup) return;

    var infoWrapper = getInfoWrapper();
    if (!infoWrapper) return;

    // Clip container: position:fixed for viewport-level positioning, but appended
    // inside .main-product-page (NOT document.body) — .main-product-page has
    // isolation:isolate on mobile (section-main-product.css), which opens its own
    // stacking context. That traps .product__info-wrapper's z-index:203 inside it,
    // uncomparable to anything outside. Appending this to document.body put it in
    // the ROOT stacking context instead, where it only had to beat .main-product-page
    // ITSELF (z-index:4 on mobile) — which z-index:202 always wins, so the popup
    // painted above the whole product page (info-wrapper included) regardless of the
    // 202-vs-203 numbers, showing through on top instead of being covered as it
    // closes. Appending inside .main-product-page puts both in the same isolated
    // context, where 202 vs 203 actually applies.
    var mainProductPage = infoWrapper.closest('.main-product-page') || document.body;
    colorClipContainer = document.createElement('div');
    colorClipContainer.className = 'color-popup-clip';
    mainProductPage.appendChild(colorClipContainer);

    // Backdrop inside clip container — fills it, clickable to close
    backdrop = document.createElement('div');
    backdrop.className = 'color-peek-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.style.display = 'none';
    backdrop.addEventListener('click', function () {
      closePopup();
    });
    colorClipContainer.appendChild(backdrop);

    popup = document.createElement('div');
    popup.className = 'color-image-popup';
    popup.innerHTML =
      '<div class="color-image-popup__handle-bar"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="2" viewBox="0 0 24 2" fill="none"><path d="M1 1H23" stroke="#DAD4D3" stroke-width="2" stroke-linecap="round"/></svg></div>' +
      '<div class="color-image-popup__color body-text-medium"></div>' +
      '<div class="color-image-popup__track"></div>';

    colorClipContainer.appendChild(popup);

    trackEl = popup.querySelector('.color-image-popup__track');
    colorEl = popup.querySelector('.color-image-popup__color');

    // Click handle bar to close with animation
    popup.querySelector('.color-image-popup__handle-bar').addEventListener('click', function () {
      closePopup();
    });

    // Swipe-down to close
    var touchStartY = 0;
    popup.addEventListener('touchstart', function (e) {
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    popup.addEventListener('touchend', function (e) {
      var delta = e.changedTouches[0].clientY - touchStartY;
      if (delta > 50) closePopup();
    }, { passive: true });
  }

  function getAllSwatches() {
    return Array.from(
      document.querySelectorAll('.product-paralax .product__title-swatches .color-swatch')
    );
  }

  function showSwatchImageInGallery(imageUrl) {
    if (!imageUrl) return false;

    var mediaGallery = document.querySelector('media-gallery');
    if (mediaGallery && typeof mediaGallery.setActiveMediaByImageUrl === 'function') {
      var matched = mediaGallery.setActiveMediaByImageUrl(imageUrl);
      if (matched) return true;
    }

    var hoverContainer = document.querySelector('.swtach-hover-image');
    if (hoverContainer) {
      hoverContainer.innerHTML = '<img src="' + imageUrl + '" alt="" loading="eager" style="width:100%;height:100%;object-fit:cover;">';
      hoverContainer.classList.add('is-visible');
      return true;
    }

    return false;
  }

  window.openColorSwatchPopup = function (clickedSwatch) {
    openPopup(clickedSwatch);
  };

  function openPopup(clickedSwatch) {
    var infoWrapper = getInfoWrapper();
    if (!infoWrapper) return;

    // If size popup is open, close it first
    if (window.sizePeekClose && typeof window.sizePeekClose === 'function') {
      var sizeWrapper = document.querySelector('.size-popup-wrapper');
      if (sizeWrapper && sizeWrapper.classList.contains('is-open')) {
        window.sizePeekClose();
        // Wait for size popup to close before opening color popup
        setTimeout(function () {
          openColorPopup(clickedSwatch, infoWrapper);
        }, 400);
        return;
      }
    }

    openColorPopup(clickedSwatch, infoWrapper);
  }

  function openColorPopup(clickedSwatch, infoWrapper) {
    // Add class to prevent snap scrolling FIRST — before any DOM changes
    infoWrapper.classList.add('color-peek-open');

    // Re-sync so the scroll-close listener's first comparison is against the
    // real current position, not a value left stale from a previous open/close
    // cycle (or 0 from before this popup ever opened) — a stale lastScrollY
    // reads as "scrolled down" on the very next scroll tick and closes the
    // popup the instant it opens.
    lastScrollY = window.scrollY;

    buildPopup();
    if (!trackEl) return;

    var swatches = getAllSwatches();

    // Build carousel tiles from all swatches that have a data-image
    trackEl.innerHTML = '';
    var activeIndex = 0;
    var tiles = [];

    swatches.forEach(function (swatch, i) {
      var imageUrl = swatch.getAttribute('data-image');
      if (!imageUrl) return;

      var isActive = swatch === clickedSwatch;
      if (isActive) activeIndex = tiles.length;

      var colorName = swatch.getAttribute('data-color-name') || '';

      var tile = document.createElement('a');
      var isSoldOut = swatch.classList.contains('is-disabled');
      tile.className = 'color-image-popup__tile' + (isActive ? ' is-active' : '') + (isSoldOut ? ' is-sold-out' : '');
      var swatchHref = swatch.getAttribute('href');
      var optionInputId = swatch.getAttribute('data-option-input-id');
      tile.href = swatchHref || '#';
      tile.setAttribute('data-color-name', colorName);
      tile.setAttribute('data-image', imageUrl);
      if (optionInputId) tile.setAttribute('data-option-input-id', optionInputId);
      tile.innerHTML = '<img src="' + imageUrl + '" alt="" loading="eager">' + (isSoldOut ? '<div class="color-image-popup__sold-out-overlay" aria-hidden="true"></div>' : '');

      // Hover on tile → show image in the main product media gallery (desktop only)
      tile.addEventListener('mouseenter', function () {
        if (isMobile()) return;
        var hoverContainer = document.querySelector('.swtach-hover-image');
        if (hoverContainer) {
          hoverContainer.innerHTML = '<img src="' + imageUrl + '" alt="" loading="eager" style="width:100%;height:100%;object-fit:cover;">';
          hoverContainer.classList.add('is-visible');
        }
      });

      tile.addEventListener('mouseleave', function () {
        if (isMobile()) return;
        var hoverContainer = document.querySelector('.swtach-hover-image');
        if (hoverContainer) {
          hoverContainer.innerHTML = '';
          hoverContainer.classList.remove('is-visible');
        }
      });

      // Tap a tile → update active state + color name (don't navigate immediately)
      tile.addEventListener('click', function (e) {
        e.preventDefault();
        tiles.forEach(function (t) { t.classList.remove('is-active'); });
        tile.classList.add('is-active');
        if (colorEl) colorEl.textContent = colorName;
        // Sync active class on title swatches
        getAllSwatches().forEach(function (s) {
          var sName = s.getAttribute('data-color-name') || '';
          s.classList.toggle('active', sName.toLowerCase() === colorName.toLowerCase());
        });
        showSwatchImageInGallery(imageUrl);
        if (!swatchHref && optionInputId) {
          var input = document.getElementById(optionInputId) || swatch.control;
          if (input) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            closePopup({ confirmed: true });
          }
          return;
        }
        // Title-swatch path: no href, no optionInputId — find the real color label by name
        if (!swatchHref && !optionInputId) {
          var realLabel = Array.from(
            document.querySelectorAll('variant-selects .color-swatch--input')
          ).find(function (el) {
            return (el.getAttribute('data-color-name') || '').toLowerCase() === colorName.toLowerCase();
          });
          if (realLabel) {
            var realInput = document.getElementById(realLabel.getAttribute('data-option-input-id') || realLabel.getAttribute('for'));
            if (realInput) {
              realInput.checked = true;
              realInput.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
              realLabel.click();
            }
            closePopup({ confirmed: true });
            return;
          }
        }
        // Navigate after short delay so user sees the selection
        setTimeout(function () { window.location.href = tile.href; }, 200);
      });

      trackEl.appendChild(tile);
      tiles.push(tile);
    });

    // Set initial color name from the clicked swatch
    if (colorEl) {
      colorEl.textContent = clickedSwatch.getAttribute('data-color-name') || '';
    }

    // Show clicked swatch image in gallery immediately on mobile
    var clickedImageUrl = clickedSwatch.getAttribute('data-image');
    showSwatchImageInGallery(clickedImageUrl);

    // Size clip container to gallery area (0 → info bar top), overflow:hidden clips popup
    var infoRect = infoWrapper.getBoundingClientRect();
    if (colorClipContainer) {
      colorClipContainer.style.height = infoRect.top + 'px';
    }
    if (popup) popup.style.bottom = '0px';

    // Force reflow then open
    if (popup) {
      popup.getBoundingClientRect();
      popup.classList.add('is-open');
    }

    // Show backdrop with fade
    if (backdrop) {
      backdrop.style.display = 'block';
      requestAnimationFrame(function () {
        backdrop.classList.add('is-visible');
      });
    }

    // Suppress the scroll-close listener while we scroll the track
    suppressScrollClose = true;
    setTimeout(function () { suppressScrollClose = false; }, 400);

    // Scroll the active tile as far left as possible (align to start)
    setTimeout(function () {
      if (tiles[activeIndex] && trackEl) {
        var tile = tiles[activeIndex];
        var trackRect = trackEl.getBoundingClientRect();
        var tileRect = tile.getBoundingClientRect();
        var offset = tileRect.left - trackRect.left;
        trackEl.scrollLeft += offset;
      }
    }, 60);
  }

  function resetTitleSwatchActiveToCurrentPage() {
    var swatches = getAllSwatches();
    var currentPath = window.location.pathname;
    var matched = false;
    // First pass: match by href (connector swatches — <a> elements)
    swatches.forEach(function (s) {
      s.classList.remove('active');
      var href = s.getAttribute('href');
      if (href && (href === currentPath || currentPath.indexOf(href) === 0)) {
        s.classList.add('active');
        matched = true;
      }
    });
    // Second pass: if no href match (title-only <span> swatches), restore original active
    if (!matched) {
      swatches.forEach(function (s) {
        var origActive = s.getAttribute('data-was-active');
        if (origActive === 'true') s.classList.add('active');
      });
    }
  }

  function closePopup(opts) {
    var instant = opts && opts.instant;
    var confirmed = opts && opts.confirmed;
    var clipClose = opts && opts.clipClose;
    // Re-sync clip height to the info wrapper's CURRENT top before animating out —
    // it's only ever set once, at open (openColorPopup). If the wrapper's top moved
    // since then (header hide/show, scroll settle, viewport change) the clip stays
    // at the stale open-time height, so the close slide-down gets cut off at the old
    // boundary instead of tucking cleanly under the panel's actual current position.
    var infoWrapperForClose = getInfoWrapper();
    if (colorClipContainer && infoWrapperForClose) {
      colorClipContainer.style.height = infoWrapperForClose.getBoundingClientRect().top + 'px';
    }
    if (backdrop) {
      backdrop.classList.remove('is-visible');
      if (instant) {
        backdrop.style.display = 'none';
      } else {
        setTimeout(function () { backdrop.style.display = 'none'; }, 350);
      }
    }
    if (popup) {
      if (instant) {
        popup.style.transition = 'none';
        popup.classList.remove('is-open');
        // Re-enable transition next frame so future open/close animations work
        requestAnimationFrame(function () { if (popup) popup.style.transition = ''; });
      } else if (clipClose) {
        // Disable CSS transform transition — clip container height will mask the popup
        // as the info panel scrolls over it. No competing slide-down animation.
        popup.style.transition = 'none';
        popup.classList.remove('is-open');
        requestAnimationFrame(function () { if (popup) popup.style.transition = ''; });
      } else {
        popup.classList.remove('is-open');
      }
    }
    // Remove class to allow snap scrolling again
    var infoWrapper = getInfoWrapper();
    if (infoWrapper) {
      infoWrapper.classList.remove('color-peek-open');
    }
    // If dismissed without selecting a color, reset active swatch to current page product
    if (!confirmed) {
      resetTitleSwatchActiveToCurrentPage();
      var hoverContainer = document.querySelector('.swtach-hover-image');
      if (hoverContainer) {
        hoverContainer.innerHTML = '';
        hoverContainer.classList.remove('is-visible');
      }
    }
  }

  window.closeColorPopup = function (opts) { closePopup(opts); };

  // Close when user scrolls down (away from collapsed state)
  var lastScrollY = 0;
  window.addEventListener('scroll', function () {
    if (suppressScrollClose) return;

    if (popup && popup.classList.contains('is-open')) {
      var currentScrollY = window.scrollY;
      var panelReveal = typeof window.ScrollTrigger !== 'undefined' && window.ScrollTrigger.getById
        ? window.ScrollTrigger.getById('pdp-panel-reveal')
        : null;
      var stillCollapsed = panelReveal ? panelReveal.progress < 0.5 : true;
      // Close if scrolling down OR if no longer collapsed
      if (currentScrollY > lastScrollY || !stillCollapsed) {
        closePopup();
      }
      lastScrollY = currentScrollY;
    }
  }, { passive: true });

  function initSwatchTapHandler() {
    // Bind on document (not the .product__title-swatches node itself) — selecting a size
    // triggers product-info.js to replace the price block's innerHTML, which destroys and
    // recreates this container and would silently drop a listener bound directly to it.
    function handleTitleSwatchInteraction(e) {
      if (!isMobile()) return;

      var titleSwatches = e.target.closest('.product-paralax .product__title-swatches');
      if (!titleSwatches) return;

      var swatch = e.target.closest('.color-swatch');
      if (!swatch) return;

      var imageUrl = swatch.getAttribute('data-image');

      if (e.type === 'click' && lastHandledSwatch === swatch && Date.now() - lastHandledSwatchAt < 700) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      lastHandledSwatch = swatch;
      lastHandledSwatchAt = Date.now();

      // Update active class on title swatches immediately
      getAllSwatches().forEach(function (s) { s.classList.remove('active'); });
      swatch.classList.add('active');

      // Show image in gallery immediately, if one is available
      if (imageUrl) showSwatchImageInGallery(imageUrl);

      // Always open the color popup — never navigate away to the connector's PDP
      if (typeof window.openColorSwatchPopup === 'function') {
        window.openColorSwatchPopup(swatch);
      }
    }

    document.addEventListener('touchstart', handleTitleSwatchInteraction, { passive: false });
    document.addEventListener('click', handleTitleSwatchInteraction);

    // Close color popup when ATC button is clicked (let normal ATC flow handle size popup)
    var atcButton = document.querySelector('.product-paralax .product-form__submit');
    if (atcButton) {
      atcButton.addEventListener('click', function (e) {
        // Skip if we're already in the process of closing for ATC
        if (isClosingForATC) {
          isClosingForATC = false;
          return;
        }

        if (popup && popup.classList.contains('is-open')) {
          e.preventDefault();
          e.stopPropagation();
          isClosingForATC = true;
          closePopup();
          // Wait for color popup transition to complete (350ms), then trigger ATC
          setTimeout(function () {
            atcButton.click();
          }, 400);
        }
      });
    }
  }

  // Close color popup on click outside popup and outside swatches
  document.addEventListener('click', function (e) {
    if (!popup || !popup.classList.contains('is-open')) return;
    if (popup.contains(e.target)) return;
    if (e.target.closest('.color-swatch')) return;
    closePopup();
  }, true);

  function stampInitialActiveSwatches() {
    getAllSwatches().forEach(function (s) {
      s.setAttribute('data-was-active', s.classList.contains('active') ? 'true' : 'false');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      stampInitialActiveSwatches();
      initSwatchTapHandler();
    });
  } else {
    stampInitialActiveSwatches();
    initSwatchTapHandler();
  }
})();

// Sticky ATC logic moved to pdp-sticky-atc.js (loaded async)
